import React, { useState, useEffect, useMemo } from 'react';
import type { Client, Software, StaffMember, CurrentUserProfile, StaffPermissions, Plan, FeatureDefinition } from './types';
import Swal from 'sweetalert2';
import { 
  Users, 
  Target, 
  Activity, 
  Ticket, 
  Headset,
  LogOut, 
  Shield, 
  AlertTriangle, 
  Home, 
  WalletCards, 
  ClipboardList, 
  UserCheck, 
  UserPlus, 
  UserX, 
  Layers, 
  Lock,
  Eye,
  EyeOff,
  Mail, 
  Edit3, 
  Trash2,
  ChevronDown,
  Package,
  CheckCircle2,
  Clock,
  AlertCircle
} from 'lucide-react';
import {
  Dropdown,
  Tag,
  Space,
  Button,
  Modal,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Row,
  Col,
  Alert,
  Divider,
  Tooltip,
  Card,
  Pagination,
  Timeline,
  Avatar,
  Segmented,
  Descriptions,
  Badge
} from 'antd';
import type { MenuProps } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  LockOutlined,
  ArrowLeftOutlined,
  SendOutlined,
  PhoneOutlined,
  BankOutlined,
  PlusOutlined,
  FilePdfOutlined,
  CopyOutlined,
  WhatsAppOutlined,
  UploadOutlined,
  CreditCardOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  ExperimentOutlined,
  ThunderboltFilled,
  SafetyCertificateOutlined,
  FileImageOutlined,
  BellOutlined,
  ClusterOutlined,
  EyeOutlined
} from '@ant-design/icons';
import './index.css';
import { PlansManagementView } from './components/PlansManagementView';
import LicenseManagerModal from './components/LicenseManagerModal';

const API_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? (import.meta.env.VITE_API_URL || 'http://localhost:5006/api')
  : 'https://helpdesk-api.saasbuck.com/api';

function useWindowSize() {
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return size;
}

const App: React.FC = () => {
  const { width } = useWindowSize();
  const [token, setToken] = useState<string | null>(localStorage.getItem('hd_token'));
  const [userProfile, setUserProfile] = useState<CurrentUserProfile | null>(null);
  const [selectedSoftware, setSelectedSoftware] = useState<'Synapse' | 'Co-HR' | null>(null);
  const [view, setView] = useState<'clients' | 'leads' | 'tickets' | 'activity-logs' | 'billing' | 'admin-audit' | 'updates' | 'team' | 'plans'>('clients');
  // Live version/LOC/changelog data for the Updates & Changelog page — see
  // backend/server.js's /api/system-status, which aggregates this app's
  // own stats with the Synapse CRM's (fetched server-side) and the mobile
  // app's (fetched from GitHub, since it has no server of its own). Only
  // fetched when the Updates view is actually open, not on every app load.
  const [systemStatus, setSystemStatus] = useState<any>(null);
  const [systemStatusLoading, setSystemStatusLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [ticketComment, setTicketComment] = useState('');
  const [adminRemarksText, setAdminRemarksText] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedClientForLicense, setSelectedClientForLicense] = useState<any | null>(null);

  const [globalLogs, setGlobalLogs] = useState<any[]>([]);
  const [adminAuditLogs, setAdminAuditLogs] = useState<any[]>([]);
  const [billingRows, setBillingRows] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'live' | 'demo' | 'expired' | 'Open' | 'Accepted' | 'Pending' | 'Completed'>('all');
  
  const [ticketSoftwareFilter, setTicketSoftwareFilter] = useState<'all' | 'Synapse' | 'Co-HR'>('all');
  const [ticketPriorityFilter, setTicketPriorityFilter] = useState<'all' | 'Low' | 'Medium' | 'High' | 'Urgent'>('all');
  const [ticketViewMode, setTicketViewMode] = useState<'flat' | 'grouped'>('flat');

  // Tags State (replaces old feature-toggle customizations)
  const [selectedClientForTags, setSelectedClientForTags] = useState<any | null>(null);
  const [clientTags, setClientTags] = useState<string[]>([]);
  const [clientCustomNote, setClientCustomNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tagsSaving, setTagsSaving] = useState(false);

  // Per-client feature/plan-assignment state was removed along with the
  // "Features" button/modal — there's only one license now, and seats live
  // on the License activation flow instead (see activateLive below).

  // Plans & Packages State — read-only display now (see note above on why
  // create/edit/delete were removed).
  const [plans, setPlans] = useState<Plan[]>([]);
  const [featureDefinitions, setFeatureDefinitions] = useState<Record<string, FeatureDefinition[]>>({});
  const [isPlansLoading, setIsPlansLoading] = useState(false);

  // Client License & Info State (LicenseManagerModal handles both licensing and info overview)
  const [licenseModalInitialTab, setLicenseModalInitialTab] = useState<string>('info');

  // Standardized Table Pagination States
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(10);
  const [billingPage, setBillingPage] = useState(1);
  const [billingPageSize, setBillingPageSize] = useState(10);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(10);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketPageSize, setTicketPageSize] = useState(10);
  const [groupedTicketPage, setGroupedTicketPage] = useState(1);
  const [groupedTicketPageSize, setGroupedTicketPageSize] = useState(10);

  // WhatsApp Client Config State
  const [selectedClientForWhatsApp, setSelectedClientForWhatsApp] = useState<any | null>(null);
  const [whatsappConfig, setWhatsappConfig] = useState<any>({ accessToken: '', phoneNumberId: '', wabaId: '' });
  const [whatsappConfigLoading, setWhatsappConfigLoading] = useState(false);
  const [whatsappConfigSaving, setWhatsappConfigSaving] = useState(false);
  const [waTestPhone, setWaTestPhone] = useState('');
  const [waTestSending, setWaTestSending] = useState(false);
  const [authMode, setAuthMode] = useState<'password' | 'otp'>('password');
  const [loginData, setLoginData] = useState({ email: '', password: '', otp: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Team Management State
  const [teamMembers, setTeamMembers] = useState<StaffMember[]>([]);
  const [isTeamLoading, setIsTeamLoading] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  // Manual client creation (offline/phone sale — customer never touched
  // the CRM's own signup page). See POST /api/clients/create-manual.
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  const [createClientSaving, setCreateClientSaving] = useState(false);
  const EMPTY_CREATE_CLIENT_FORM = {
    name: '',
    email: '',
    phone: '',
    company: '',
    seats: '1',
    months: '12',
    pricePerUser: '',
    addons: {
      whatsappCrm: false,
      liveTracking: false,
      customDomain: false,
      prioritySla: false,
      vipOnboarding: false,
      additionalWorkspaces: 0
    }
  };
  const [createClientForm, setCreateClientForm] = useState(EMPTY_CREATE_CLIENT_FORM);

  // Gated Onboarding Journey States
  const [createClientStep, setCreateClientStep] = useState<1 | 2 | 3>(1);
  const [draftPayment, setDraftPayment] = useState<any>(null);
  const [quotationFile, setQuotationFile] = useState<File | null>(null);
  const [uploadingQuotation, setUploadingQuotation] = useState(false);
  const [paymentModeTab, setPaymentModeTab] = useState<'link' | 'offline'>('link');
  const [linkExpireHours, setLinkExpireHours] = useState<number>(48);
  const [paymentLinkExpiresAt, setPaymentLinkExpiresAt] = useState<string | null>(null);
  const [generatedPaymentLink, setGeneratedPaymentLink] = useState('');
  const [whatsappShareUrl, setWhatsappShareUrl] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [offlineForm, setOfflineForm] = useState<{
    utrNumber: string;
    bankName: string;
    notes: string;
    slipFile: File | null;
  }>({
    utrNumber: '',
    bankName: '',
    notes: '',
    slipFile: null
  });
  const [recordingOfflinePayment, setRecordingOfflinePayment] = useState(false);
  const [simulatingPayment, setSimulatingPayment] = useState(false);
  const [pendingOfflinePayments, setPendingOfflinePayments] = useState<any[]>([]);
  const [isPendingApprovalsModalOpen, setIsPendingApprovalsModalOpen] = useState(false);
  const [isApprovingOfflineId, setIsApprovingOfflineId] = useState<string | null>(null);
  const [isRejectingOfflineId, setIsRejectingOfflineId] = useState<string | null>(null);

  // Automated Subscription Renewal Alerts state (Owner)
  const [renewalAlerts, setRenewalAlerts] = useState<any[]>([]);
  const [isRenewalAlertsModalOpen, setIsRenewalAlertsModalOpen] = useState(false);
  const [loadingRenewalAlerts, setLoadingRenewalAlerts] = useState(false);

  const resetCreateClientModal = () => {
    setIsCreateClientModalOpen(false);
    setCreateClientForm(EMPTY_CREATE_CLIENT_FORM);
    setCreateClientStep(1);
    setDraftPayment(null);
    setQuotationFile(null);
    setLinkExpireHours(48);
    setPaymentLinkExpiresAt(null);
    setGeneratedPaymentLink('');
    setWhatsappShareUrl('');
    setOfflineForm({ utrNumber: '', bankName: '', notes: '', slipFile: null });
    setSimulatingPayment(false);
  };
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [staffFormData, setStaffFormData] = useState({
    name: '',
    email: '',
    role: 'support' as 'superadmin' | 'admin' | 'support' | 'developer' | 'custom',
    password: '',
    permissions: {
      manageLicenses: false,
      manageClients: false,
      manageTickets: true,
      manageWhatsApp: false,
      viewAuditLogs: false,
      manageTeam: false
    } as StaffPermissions,
    softwareAccess: ['All'] as ('All' | 'Synapse' | 'Co-HR')[]
  });

  // Deliberately not an early `return` here — every hook in this component
  // (the useEffect/useMemo calls further down, e.g. around line 800+) must
  // run unconditionally on every render. Returning early before them meant
  // this component called a different number of hooks depending on window
  // width, which crashes React with "Rendered fewer hooks than expected."
  // The actual conditional render happens at the bottom, after all hooks.
  const isMobileBlocked = width < 768;

  const fetchCurrentUserProfile = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserProfile(data);
      } else if (res.status === 401 || res.status === 403) {
        setToken(null);
        localStorage.removeItem('hd_token');
        setUserProfile(null);
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  const fetchTeamMembers = async () => {
    setIsTeamLoading(true);
    try {
      const res = await fetch(`${API_URL}/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeamMembers(data);
      }
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    } finally {
      setIsTeamLoading(false);
    }
  };

  // /api/system-status is intentionally public (same trust level as
  // /api/version and /api/health), so no Authorization header needed.
  const fetchSystemStatus = async () => {
    setSystemStatusLoading(true);
    try {
      const res = await fetch(`${API_URL}/system-status`);
      if (res.ok) {
        setSystemStatus(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch system status:', err);
    } finally {
      setSystemStatusLoading(false);
    }
  };

  const handleSaveStaffMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffFormData.email || !staffFormData.name) {
      Swal.fire('Error', 'Name and Gmail / Email address are required', 'error');
      return;
    }

    try {
      const url = editingStaffId ? `${API_URL}/team/${editingStaffId}` : `${API_URL}/team`;
      const method = editingStaffId ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(staffFormData)
      });

      const data = await res.json();
      if (res.ok) {
        Swal.fire('Success', editingStaffId ? 'Staff permissions updated successfully' : 'Staff member registered successfully', 'success');
        setIsStaffModalOpen(false);
        setEditingStaffId(null);
        fetchTeamMembers();
      } else {
        Swal.fire('Error', data.message || 'Failed to save staff member', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Server connection failed', 'error');
    }
  };

  const handleToggleStaffStatus = async (staff: StaffMember) => {
    const nextStatus = staff.status === 'active' ? 'disabled' : 'active';
    const confirmText = nextStatus === 'disabled' 
      ? `Deactivate ${staff.name}'s access? They will not be able to log in.`
      : `Re-activate ${staff.name}'s access?`;

    const result = await Swal.fire({
      title: nextStatus === 'disabled' ? 'Deactivate Account?' : 'Activate Account?',
      text: confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: nextStatus === 'disabled' ? 'Yes, Deactivate' : 'Yes, Activate',
      confirmButtonColor: nextStatus === 'disabled' ? '#dc2626' : '#16a34a'
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}/team/${staff._id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ status: nextStatus })
        });
        if (res.ok) {
          Swal.fire('Updated', `Account is now ${nextStatus}`, 'success');
          fetchTeamMembers();
        } else {
          const data = await res.json();
          Swal.fire('Error', data.message || 'Failed to update status', 'error');
        }
      } catch (err) {
        Swal.fire('Error', 'Server error', 'error');
      }
    }
  };

  const handleDeleteStaff = async (staff: StaffMember) => {
    const result = await Swal.fire({
      title: `Delete ${staff.name}?`,
      text: `This will remove ${staff.email} completely from the portal.`,
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete Staff',
      confirmButtonColor: '#dc2626'
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}/team/${staff._id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          Swal.fire('Deleted', 'Staff member deleted', 'success');
          fetchTeamMembers();
        } else {
          const data = await res.json();
          Swal.fire('Error', data.message || 'Failed to delete staff member', 'error');
        }
      } catch (err) {
        Swal.fire('Error', 'Server error', 'error');
      }
    }
  };

  const fetchPlans = async (sw?: string) => {
    setIsPlansLoading(true);
    try {
      const targetSw = sw || selectedSoftware || 'Synapse';
      const res = await fetch(`${API_URL}/plans?software=${targetSw}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
        if (data.featureDefinitions) {
          setFeatureDefinitions(data.featureDefinitions);
        }
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setIsPlansLoading(false);
    }
  };

  // Plan create/edit/delete UI was removed — there's one fixed license per
  // software now, nothing to configure via CRUD. Price changes go directly
  // through the database/a script rather than an admin form.
  //
  // Per-client feature/plan-assignment modal was also removed — see License
  // activate/extend below, which now carries seats too.

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginData.email) {
      Swal.fire('Error', 'Please enter your email', 'error');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch(`${API_URL}/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginData.email })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOtpSent(true);
        if (data.devOtp) {
          setLoginData(prev => ({ ...prev, otp: data.devOtp }));
          Swal.fire('OTP Generated', `Code: ${data.devOtp} (Auto-filled)`, 'success');
        } else {
          Swal.fire('OTP Sent', 'Check your email for the OTP code', 'success');
        }
      } else {
        Swal.fire('Access Denied', data.message || 'This email is not authorized to access this portal.', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to connect to server', 'error');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginData.email) {
      Swal.fire('Error', 'Please enter your email', 'error');
      return;
    }

    if (authMode === 'password' && !loginData.password) {
      Swal.fire('Error', 'Please enter your password', 'error');
      return;
    }

    if (authMode === 'otp' && !loginData.otp) {
      Swal.fire('Error', 'Please enter the 6-digit OTP code', 'error');
      return;
    }

    setLoginLoading(true);
    try {
      const payload = authMode === 'password'
        ? { email: loginData.email, password: loginData.password }
        : { email: loginData.email, otp: loginData.otp };

      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('hd_token', data.token);
        setToken(data.token);
        if (data.user) setUserProfile(data.user);
        Swal.fire('Welcome!', `Logged in as ${data.user?.name || data.user?.role || 'Staff'}`, 'success');
      } else {
        Swal.fire('Login Failed', data.message || 'Invalid credentials', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to connect to server', 'error');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: 'Log out of Help Desk?',
      text: 'You will need to sign in again with your password or OTP code.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1877f2',
      cancelButtonColor: '#65676b',
      confirmButtonText: 'Yes, Log Out',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setToken(null);
      setUserProfile(null);
      localStorage.removeItem('hd_token');
    }
  };

  const fetchClients = async (software: Software) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/clients?software=${software}&_t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store'
      });
      const data = await response.json();
      if (response.ok) {
        setClients(data);
      } else if (response.status === 401) {
        setToken(null);
        localStorage.removeItem('hd_token');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setTickets(data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (ticketId: string, status: string, adminRemarks?: string) => {
    try {
      const res = await fetch(`${API_URL}/tickets/${ticketId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status, adminRemarks })
      });
      if (res.ok) {
        const updatedTicket = await res.json();
        setSelectedTicket(updatedTicket);
        fetchTickets();
      }
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  const fetchGlobalLogs = async (software: Software) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/software-logs?software=${software}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setGlobalLogs(data);
      }
    } catch (error) {
      console.error('Error fetching global logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminAuditLogs = async (software?: Software | null) => {
    setLoading(true);
    try {
      const url = software ? `${API_URL}/admin-audit?software=${software}` : `${API_URL}/admin-audit`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setAdminAuditLogs(data);
    } catch (error) {
      console.error('Error fetching admin audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingDashboard = async (software?: Software | null) => {
    setLoading(true);
    try {
      const url = software ? `${API_URL}/billing-dashboard?software=${software}` : `${API_URL}/billing-dashboard`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setBillingRows(data);
    } catch (error) {
      console.error('Error fetching billing dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/dashboard-analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setAnalytics(data);
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingOfflinePayments = async () => {
    if (!token || !userProfile?.isSuperAdmin) return;
    try {
      const response = await fetch(`${API_URL}/payments/offline-pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setPendingOfflinePayments(data.pendingPayments || []);
      }
    } catch (error) {
      console.error('Error fetching pending offline payments:', error);
    }
  };

  const fetchRenewalAlerts = async () => {
    if (!token || !userProfile?.isSuperAdmin) return;
    setLoadingRenewalAlerts(true);
    try {
      const response = await fetch(`${API_URL}/payments/renewal-alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setRenewalAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching renewal alerts:', error);
    } finally {
      setLoadingRenewalAlerts(false);
    }
  };


  const fetchTicketDetail = async (ticketId: string) => {
    try {
      const response = await fetch(`${API_URL}/tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedTicket(data);
        setAdminRemarksText(data.adminRemarks || '');
      }
    } catch (error) {
      console.error('Error fetching ticket detail:', error);
    }
  };

  const addTicketComment = async () => {
    if (!selectedTicket || !ticketComment.trim()) return;
    try {
      const response = await fetch(`${API_URL}/tickets/${selectedTicket._id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: ticketComment.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedTicket(data);
        setTicketComment('');
        fetchTickets();
      } else {
        Swal.fire('Error', data.message || 'Failed to add comment', 'error');
      }
    } catch {
      Swal.fire('Error', 'Failed to add comment', 'error');
    }
  };

  useEffect(() => {
    if (token) {
      if (!userProfile) {
        fetchCurrentUserProfile();
      }
      if (selectedSoftware) {
        fetchClients(selectedSoftware);
        if (view === 'activity-logs') {
          fetchGlobalLogs(selectedSoftware);
        } else if (view === 'billing') {
          fetchBillingDashboard(selectedSoftware);
        }
      } else if (view === 'clients') {
        fetchDashboardAnalytics();
      } else if (view === 'billing') {
        fetchBillingDashboard(null);
      } else if (view === 'admin-audit') {
        fetchAdminAuditLogs(null);
      } else if (view === 'team') {
        fetchTeamMembers();
      }
      if (userProfile?.isSuperAdmin) {
        fetchPendingOfflinePayments();
        fetchRenewalAlerts();
      }
      // Only fetch tickets when viewing the tickets section or on first auth
      if (view === 'tickets' || (!selectedSoftware && view === 'clients')) {
        fetchTickets();
      }
    }
  }, [selectedSoftware, token, view, userProfile?.isSuperAdmin]);


  const groupedTickets = React.useMemo(() => {
    const groups: { [key: string]: any } = {};
    tickets.forEach(ticket => {
      const key = ticket.clientId || ticket.clientEmail;
      if (!groups[key]) {
        groups[key] = {
          // clientId is undefined for guest/no-account tickets (grouped by
          // email only) — using it as the React `key`/selection identifier
          // meant every such group collided on the same undefined value,
          // which is what threw the "unique key" warning and crashed with
          // "Node cannot be found in the current page" on reconciliation.
          // groupKey is the same value already used to bucket tickets above,
          // so it's always defined and always unique per group.
          groupKey: key,
          clientId: ticket.clientId,
          clientName: ticket.clientName,
          clientEmail: ticket.clientEmail,
          software: ticket.software,
          ticketCount: 0,
          tickets: []
        };
      }
      groups[key].tickets.push(ticket);
      groups[key].ticketCount++;
    });
    return Object.values(groups);
  }, [tickets]);

  const openLicenseManager = (client: any, initialTab: string = 'info') => {
    setLicenseModalInitialTab(initialTab || 'info');
    setSelectedClientForLicense(client);
  };

  // Step 1: Create draft and move to quotation upload
  const handleProceedToQuotation = async () => {
    const { name, email, phone, company, seats, months, pricePerUser } = createClientForm;
    if (!name.trim() || !email.trim() || !company.trim()) {
      Swal.fire('Missing details', 'Name, email and company are required.', 'error');
      return;
    }
    const parsedSeats = parseInt(seats, 10);
    if (!Number.isFinite(parsedSeats) || parsedSeats < 1) {
      Swal.fire('Invalid seats', 'Seats must be a positive integer.', 'error');
      return;
    }
    const parsedMonths = parseInt(months, 10);
    if (!Number.isFinite(parsedMonths) || parsedMonths < 1) {
      Swal.fire('Invalid duration', 'Months must be a positive integer.', 'error');
      return;
    }

    setCreateClientSaving(true);
    try {
      const res = await fetch(`${API_URL}/payments/onboard-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          software: 'Synapse',
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          company: company.trim(),
          seats: parsedSeats,
          months: parsedMonths,
          pricePerUser: pricePerUser ? Number(pricePerUser) : 499,
          planName: 'Standard Plan',
          addons: createClientForm.addons
        })
      });
      const resData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok && resData.success) {
        setDraftPayment(resData.payment);
        setCreateClientStep(2);
      } else {
        Swal.fire('Error', resData.message || 'Failed to initialize draft', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error initializing draft', 'error');
    } finally {
      setCreateClientSaving(false);
    }
  };

  // Step 2: Upload quotation PDF
  const handleUploadQuotation = async () => {
    if (!quotationFile) {
      Swal.fire('Quotation Required', 'Please select a quotation PDF file to upload.', 'warning');
      return;
    }
    if (!draftPayment?._id) {
      Swal.fire('Error', 'Draft payment session missing. Please start over.', 'error');
      return;
    }

    setUploadingQuotation(true);
    try {
      const formData = new FormData();
      formData.append('quotation', quotationFile);

      const res = await fetch(`${API_URL}/payments/${draftPayment._id}/upload-quotation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const resData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok && resData.success) {
        setDraftPayment(resData.payment);
        setCreateClientStep(3);
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Quotation verified! Payment options unlocked.',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Upload Failed', resData.message || 'Failed to upload quotation', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error uploading quotation', 'error');
    } finally {
      setUploadingQuotation(false);
    }
  };

  // Step 2 Quick Action: Auto-load & verify pre-generated sample quotation
  const handleUseSampleQuotation = async () => {
    if (!draftPayment?._id) return;
    setUploadingQuotation(true);
    try {
      const sampleRes = await fetch('/sample-quotation.pdf');
      const blob = await sampleRes.blob();
      const file = new File([blob], 'Sample_Saasbuck_Quotation.pdf', { type: 'application/pdf' });
      setQuotationFile(file);

      const formData = new FormData();
      formData.append('quotation', file);

      const res = await fetch(`${API_URL}/payments/${draftPayment._id}/upload-quotation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const resData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok && resData.success) {
        setDraftPayment(resData.payment);
        setCreateClientStep(3);
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Sample quotation attached & verified!',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Upload Failed', resData.message || 'Failed to upload sample quotation', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to load sample quotation PDF', 'error');
    } finally {
      setUploadingQuotation(false);
    }
  };

  // Step 2 Quick Action: Auto-generate official Saasbuck quotation with all add-ons
  const handleAutoGenerateQuotation = async () => {
    if (!draftPayment?._id) return;
    setUploadingQuotation(true);
    try {
      const res = await fetch(`${API_URL}/payments/${draftPayment._id}/auto-quotation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const resData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok && resData.success) {
        setDraftPayment(resData.payment);
        setCreateClientStep(3);
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Official quotation generated with all add-ons! Payment options unlocked.',
          timer: 2500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Quotation Failed', resData.message || 'Failed to auto-generate quotation', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error generating quotation', 'error');
    } finally {
      setUploadingQuotation(false);
    }
  };

  const handlePreviewQuotation = () => {
    if (!draftPayment?._id) return;
    window.open(`${API_URL}/payments/quotation/${draftPayment._id}/view`, '_blank');
  };

  // Step 3A: Generate Razorpay Link
  const handleGeneratePaymentLink = async () => {
    if (!draftPayment?._id) return;
    setGeneratingLink(true);
    try {
      const res = await fetch(`${API_URL}/payments/${draftPayment._id}/send-payment-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ expireHours: linkExpireHours })
      });
      const resData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok && resData.success) {
        setGeneratedPaymentLink(resData.paymentLink);
        setPaymentLinkExpiresAt(resData.expiresAt || null);
        const phone = draftPayment.clientPhone || createClientForm.phone || '';
        const expiryFormatted = resData.expiresAt 
          ? ` (Valid for ${linkExpireHours} hours until ${new Date(resData.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, ${new Date(resData.expiresAt).toLocaleDateString()})` 
          : '';
        const msg = encodeURIComponent(`Hello ${createClientForm.name}, here is your official Saasbuck payment link for ${createClientForm.company}${expiryFormatted}: ${resData.paymentLink}`);
        setWhatsappShareUrl(`https://api.whatsapp.com/send?phone=${phone.replace(/[^0-9]/g, '')}&text=${msg}`);
      } else {
        Swal.fire('Error', resData.message || 'Failed to generate payment link', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error generating link', 'error');
    } finally {
      setGeneratingLink(false);
    }
  };

  // Step 3 Test Simulation: Instant Webhook Activation
  const handleSimulatePayment = async () => {
    if (!draftPayment?._id) return;
    setSimulatingPayment(true);
    try {
      const res = await fetch(`${API_URL}/payments/${draftPayment._id}/simulate-webhook-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const resData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok && resData.success) {
        setDraftPayment(resData.payment);
        if (selectedSoftware) fetchClients(selectedSoftware);
        Swal.fire({
          title: '🎉 Payment Verified & Client Activated!',
          html: `
            <div style="text-align: left; font-size: 0.85rem; line-height: 1.6;">
              <p style="color: #059669; font-weight: 600;">✓ Synapse subscription activated for ${resData.payment.months} month(s)</p>
              <p><strong>Official Tax Invoice:</strong> <a href="${API_URL}${resData.invoiceUrl}" target="_blank" style="color: #2563eb; text-decoration: underline;">${resData.invoiceNumber}</a></p>
              <div style="margin-top: 12px; padding: 10px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px;">
                <div style="font-weight: 600; font-size: 0.78rem; color: #475569; margin-bottom: 4px;">Client Password Setup / Welcome Link:</div>
                <div style="font-family: monospace; font-size: 0.76rem; word-break: break-all; color: #1e293b;">
                  ${resData.activationUrl}
                </div>
              </div>
            </div>
          `,
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'Open Password Setup',
          cancelButtonText: 'View Tax Invoice',
          confirmButtonColor: '#2563eb',
          cancelButtonColor: '#0f172a'
        }).then((result) => {
          if (result.isConfirmed) {
            window.open(resData.activationUrl, '_blank');
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            window.open(`${API_URL}${resData.invoiceUrl}`, '_blank');
          }
          resetCreateClientModal();
        });
      } else {
        Swal.fire('Simulation Error', resData.message || 'Failed to simulate payment', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error during simulation', 'error');
    } finally {
      setSimulatingPayment(false);
    }
  };

  // Step 3B: Record Offline Bank Transfer
  const handleRecordOfflinePayment = async () => {
    if (!offlineForm.utrNumber.trim()) {
      Swal.fire('Missing UTR', 'Bank Reference / UTR Number is required for offline verification.', 'warning');
      return;
    }
    if (!offlineForm.bankName.trim()) {
      Swal.fire('Missing Bank', 'Please enter the bank name.', 'warning');
      return;
    }
    if (!draftPayment?._id) return;

    setRecordingOfflinePayment(true);
    try {
      const res = await fetch(`${API_URL}/payments/${draftPayment._id}/record-offline-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          utrNumber: offlineForm.utrNumber.trim(),
          bankName: offlineForm.bankName.trim(),
          paymentMode: 'bank_transfer',
          remark: offlineForm.notes.trim()
        })
      });
      const resData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
      if (res.ok && resData.success) {
        // If a payment slip/receipt was attached, upload it
        if (offlineForm.slipFile && draftPayment?._id) {
          try {
            const formData = new FormData();
            formData.append('slip', offlineForm.slipFile);
            await fetch(`${API_URL}/payments/${draftPayment._id}/upload-slip`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: formData
            });
          } catch (slipErr) {
            console.warn('Failed to upload payment slip:', slipErr);
          }
        }

        if (resData.pendingApproval) {
          Swal.fire({
            icon: 'info',
            title: 'Submitted for Owner Verification',
            html: `
              <div style="text-align: left; font-size: 0.85rem; line-height: 1.6;">
                <p>The offline payment (UTR: <strong>${offlineForm.utrNumber.trim()}</strong>) has been queued for bank verification.</p>
                <div style="margin-top: 10px; padding: 10px; background: #fffbeb; border: 1px solid #fef3c7; border-radius: 6px; color: #92400e;">
                  <strong>Anti-Fraud Protection:</strong> Client subscription activation and Tax Invoice generation will take place automatically once the Saasbuck Owner verifies the deposit in the company bank account.
                </div>
              </div>
            `,
            confirmButtonColor: '#2563eb'
          });
        } else {
          Swal.fire('Client Activated!', 'Offline payment recorded and approved. Official GST Tax Invoice generated and Welcome email with password setup link sent to client.', 'success');
        }
        resetCreateClientModal();
        if (selectedSoftware) fetchClients(selectedSoftware);
        if (userProfile?.isSuperAdmin) fetchPendingOfflinePayments();
        if (resData.client) openLicenseManager(resData.client, 'billing');
      } else {
        Swal.fire('Verification Failed', resData.message || 'Failed to record offline payment', 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Network error recording payment', 'error');
    } finally {
      setRecordingOfflinePayment(false);
    }
  };

  const handleApproveFromPendingModal = async (payment: any) => {
    const confirm = await Swal.fire({
      title: 'Approve Offline Payment?',
      html: `
        <div style="text-align: left; font-size: 0.85rem; line-height: 1.6;">
          <p>Client: <strong>${payment.clientName} (${payment.company})</strong></p>
          <p>Amount: <strong>₹${Number(payment.totalAmount || 0).toLocaleString('en-IN')}</strong></p>
          <p>Bank: <strong>${payment.bankName}</strong> | UTR: <code>${payment.utrNumber}</code></p>
          <p style="color: #059669; font-weight: 600;">Have you confirmed funds deposited into the Saasbuck bank account?</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Verify & Activate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#64748b'
    });
    if (!confirm.isConfirmed) return;

    setIsApprovingOfflineId(payment._id);
    try {
      const res = await fetch(`${API_URL}/payments/${payment._id}/approve-offline-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire('✓ Approved & Activated!', 'Payment verified and client subscription activated.', 'success');
        fetchPendingOfflinePayments();
        if (selectedSoftware) fetchClients(selectedSoftware);
      } else {
        Swal.fire('Error', data.message || 'Failed to approve payment.', 'error');
      }
    } catch (err: any) {
      Swal.fire('Error', err.message || 'Network error.', 'error');
    } finally {
      setIsApprovingOfflineId(null);
    }
  };

  const handleRejectFromPendingModal = async (payment: any) => {
    const { value: reason } = await Swal.fire({
      title: 'Reject Offline Payment',
      input: 'text',
      inputLabel: `Reason for rejecting payment from ${payment.clientName} (${payment.company})`,
      inputPlaceholder: 'e.g. Deposit not received in bank account',
      inputValidator: (val) => {
        if (!val || !val.trim()) return 'Reason is required';
      },
      showCancelButton: true,
      confirmButtonText: 'Reject Payment',
      confirmButtonColor: '#dc2626'
    });
    if (!reason) return;

    setIsRejectingOfflineId(payment._id);
    try {
      const res = await fetch(`${API_URL}/payments/${payment._id}/reject-offline-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: reason.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire('Rejected', 'The offline payment has been marked as rejected.', 'info');
        fetchPendingOfflinePayments();
        if (selectedSoftware) fetchClients(selectedSoftware);
      } else {
        Swal.fire('Error', data.message || 'Failed to reject payment.', 'error');
      }
    } catch (err: any) {
      Swal.fire('Error', err.message || 'Network error.', 'error');
    } finally {
      setIsRejectingOfflineId(null);
    }
  };




  const deleteClient = async (client: any) => {
    const { value: remark } = await Swal.fire({
      title: 'Are you sure?',
      text: `Deleting ${client.name || client.username} is permanent! Reason for deletion:`,
      input: 'text',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, hard delete!',
      inputValidator: (value) => {
        if (!value) return 'You need to write a reason!'
      }
    });

    if (remark) {
      Swal.fire({
        title: 'Deleting Tenant Data...',
        html: `
          <div style="margin-top: 15px;">
            <div id="delete-progress-text" style="font-size: 0.9rem; color: #64748b; margin-bottom: 8px;">Initializing secure teardown...</div>
            <div style="width: 100%; background-color: #f1f5f9; border-radius: 4px; overflow: hidden; height: 8px;">
              <div id="delete-progress-bar" style="width: 0%; height: 100%; background-color: #ef4444; transition: width 0.4s ease;"></div>
            </div>
          </div>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
          const steps = [
            "Purging user records...",
            "Removing associated leads...",
            "Cleaning up system data...",
            "Finalizing deletion..."
          ];
          let step = 0;
          const bar = document.getElementById('delete-progress-bar');
          const text = document.getElementById('delete-progress-text');
          
          const interval = setInterval(() => {
            if (step < steps.length) {
              if (text) text.innerText = steps[step];
              if (bar) bar.style.width = `${20 + (step * 20)}%`;
              step++;
            }
          }, 800);
          
          (Swal.getPopup() as any)._progressInterval = interval;
        },
        willClose: () => {
          clearInterval((Swal.getPopup() as any)._progressInterval);
        }
      });

      try {
        const res = await fetch(`${API_URL}/clients/${client._id}?software=${encodeURIComponent(client.software || selectedSoftware!)}&remark=${encodeURIComponent(remark)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        await new Promise(r => setTimeout(r, 3200));

        if (res.ok) {
          const bar = document.getElementById('delete-progress-bar');
          const text = document.getElementById('delete-progress-text');
          if (bar) bar.style.width = '100%';
          if (text) text.innerText = "Completed!";
          await new Promise(r => setTimeout(r, 400));
          
          Swal.fire('Deleted!', 'All tenant data has been completely wiped.', 'success');
          fetchClients(selectedSoftware!);
        } else {
          const errData = await res.json().catch(() => ({ message: 'Unexpected server response' }));
          Swal.fire('Error', errData.message || 'Failed to delete client', 'error');
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to delete', 'error');
      }
    }
  };

  const loginAs = async (client: any) => {
    const accessWindow = window.open('', '_blank');
    if (accessWindow) {
      accessWindow.document.write('Connecting to secure session...');
    }

    try {
      const targetSoftware = client.software || selectedSoftware;
      const res = await fetch(`${API_URL}/clients/${client._id}/login-as?software=${targetSoftware}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        if (accessWindow) {
          accessWindow.location.href = `${data.redirectUrl}/?token=${data.token}`;
        }
      } else {
        if (accessWindow) accessWindow.close();
        Swal.fire('Error', data.message, 'error');
      }
    } catch (err) {
      if (accessWindow) accessWindow.close();
      Swal.fire('Error', 'Failed to generate access link', 'error');
    }
  };

  const filteredClients = clients.filter(client => {
    const name = client.name || client.username || '';
    const email = client.email || '';
    const phone = client.phone || '';
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phone.toLowerCase().includes(searchTerm.toLowerCase());

    const isLive = client.subscriptionStatus === 'live';
    const isDemo = client.subscriptionStatus === 'demo' || (!client.subscriptionStatus && !client.expiryDate);
    const expiry = client.expiryDate ? new Date(client.expiryDate) : null;
    const demoExpiry = client.demoExpiryDate ? new Date(client.demoExpiryDate) : null;
    const now = new Date();
    
    const trialDuration = 7 * 24 * 60 * 60 * 1000;
    const isDemoExpired = isDemo && (demoExpiry ? demoExpiry < now : new Date(client.createdAt).getTime() + trialDuration < now.getTime());
    const isLiveExpired = isLive && expiry && expiry < now;
    
    const isExpired = client.subscriptionStatus === 'expired' || isDemoExpired || isLiveExpired;

    const isClient = isLive && !isExpired;

    if (view === 'leads') {
      if (isClient) return false;
    } else {
      if (!isClient) return false;
    }

    if (statusFilter === 'all') return matchesSearch;
    if (statusFilter === 'live') return matchesSearch && isClient;
    if (statusFilter === 'demo') return matchesSearch && isDemo && !isExpired;
    if (statusFilter === 'expired') return matchesSearch && isExpired;

    return matchesSearch;
  });

  const displayedClients = [...filteredClients].sort((a, b) => {
    const isNewA = (new Date().getTime() - new Date(a.createdAt).getTime()) <= (7 * 24 * 60 * 60 * 1000);
    const isNewB = (new Date().getTime() - new Date(b.createdAt).getTime()) <= (7 * 24 * 60 * 60 * 1000);

    if (isNewA && !isNewB) return -1;
    if (!isNewA && isNewB) return 1;

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const paginatedClients = useMemo(() => {
    const start = (clientPage - 1) * clientPageSize;
    return displayedClients.slice(start, start + clientPageSize);
  }, [displayedClients, clientPage, clientPageSize]);

  const filteredTicketsList = useMemo(() => {
    return tickets.filter((t: any) => {
      const subject = t.subject || '';
      const clientName = t.clientName || '';
      const clientEmail = t.clientEmail || '';
      const matchSearch = subject.toLowerCase().includes(searchTerm.toLowerCase()) || clientName.toLowerCase().includes(searchTerm.toLowerCase()) || clientEmail.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchSoftware = ticketSoftwareFilter === 'all' || t.software === ticketSoftwareFilter;
      const matchPriority = ticketPriorityFilter === 'all' || t.priority === ticketPriorityFilter;
      return matchSearch && matchStatus && matchSoftware && matchPriority;
    });
  }, [tickets, searchTerm, statusFilter, ticketSoftwareFilter, ticketPriorityFilter]);

  const paginatedTickets = useMemo(() => {
    const start = (ticketPage - 1) * ticketPageSize;
    return filteredTicketsList.slice(start, start + ticketPageSize);
  }, [filteredTicketsList, ticketPage, ticketPageSize]);

  const filteredGroupedTickets = useMemo(() => {
    return groupedTickets.filter(g => {
      const name = g.clientName || '';
      const email = g.clientEmail || '';
      const match = name.toLowerCase().includes(searchTerm.toLowerCase()) || email.toLowerCase().includes(searchTerm.toLowerCase()) || g.tickets.some((t: any) => (t.subject || '').toLowerCase().includes(searchTerm.toLowerCase()));
      return match && (statusFilter === 'all' || g.tickets.some((t: any) => t.status === statusFilter));
    });
  }, [groupedTickets, searchTerm, statusFilter]);

  const paginatedGroupedTickets = useMemo(() => {
    const start = (groupedTicketPage - 1) * groupedTicketPageSize;
    return filteredGroupedTickets.slice(start, start + groupedTicketPageSize);
  }, [filteredGroupedTickets, groupedTicketPage, groupedTicketPageSize]);

  const paginatedBillingRows = useMemo(() => {
    const start = (billingPage - 1) * billingPageSize;
    return billingRows.slice(start, start + billingPageSize);
  }, [billingRows, billingPage, billingPageSize]);

  const paginatedLogs = useMemo(() => {
    const start = (logsPage - 1) * logsPageSize;
    return globalLogs.slice(start, start + logsPageSize);
  }, [globalLogs, logsPage, logsPageSize]);

  const paginatedAuditLogs = useMemo(() => {
    const start = (auditPage - 1) * auditPageSize;
    return adminAuditLogs.slice(start, start + auditPageSize);
  }, [adminAuditLogs, auditPage, auditPageSize]);

  if (!token) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ maxWidth: '360px', padding: '2rem', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#ffffff', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 500, color: '#0f172a', margin: '0 0 0.25rem', letterSpacing: '-0.01em' }}>Saasbuck Help Desk</h1>
            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0, fontWeight: 400 }}>Authorized Staff Portal</p>
          </div>

          {/* Authentication Mode Switcher */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem' }}>
            <Button
              type={authMode === 'password' ? 'primary' : 'default'}
              onClick={() => { setAuthMode('password'); setOtpSent(false); }}
              style={{ flex: 1, borderRadius: '4px', fontWeight: 400, height: '34px', fontSize: '0.82rem' }}
            >
              Password
            </Button>
            <Button
              type={authMode === 'otp' ? 'primary' : 'default'}
              onClick={() => { setAuthMode('otp'); }}
              style={{ flex: 1, borderRadius: '4px', fontWeight: 400, height: '34px', fontSize: '0.82rem' }}
            >
              OTP via Email
            </Button>
          </div>

          {authMode === 'password' ? (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 400, color: '#334155', marginBottom: '0.35rem' }}>Staff Gmail / Email</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={loginData.email}
                    onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.55rem 0.75rem 0.55rem 2.25rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem', fontWeight: 400, boxSizing: 'border-box' }}
                  />
                  <Mail size={15} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                </div>
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 400, color: '#334155', marginBottom: '0.35rem' }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginData.password}
                    onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.55rem 2.25rem 0.55rem 2.25rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem', fontWeight: 400, boxSizing: 'border-box' }}
                  />
                  <Lock size={15} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#64748b' }}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.35rem', lineHeight: 1.4, fontWeight: 400 }}>
                  Superadmins can sign in directly. For first-time setup, entered password will be initialized.
                </div>
              </div>

              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loginLoading}
                style={{ width: '100%', height: '36px', borderRadius: '4px', fontSize: '0.875rem', fontWeight: 400 }}
              >
                Sign In
              </Button>
            </form>
          ) : (
            <form onSubmit={otpSent ? handleLogin : handleRequestOtp}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 400, color: '#334155', marginBottom: '0.35rem' }}>Staff Gmail / Email</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={loginData.email}
                    onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                    disabled={otpSent}
                    required
                    style={{ width: '100%', padding: '0.55rem 0.75rem 0.55rem 2.25rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.875rem', fontWeight: 400, boxSizing: 'border-box' }}
                  />
                  <Mail size={15} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                </div>
              </div>

              {otpSent && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 400, color: '#334155', marginBottom: '0.35rem' }}>One-Time Password (OTP)</label>
                  <input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={loginData.otp}
                    onChange={e => setLoginData({ ...loginData, otp: e.target.value })}
                    required
                    autoFocus
                    style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '4px', border: '1px solid #2563eb', fontSize: '0.95rem', letterSpacing: '0.15em', textAlign: 'center', fontWeight: 400, boxSizing: 'border-box' }}
                  />
                </div>
              )}

              <Button 
                type="primary" 
                htmlType="submit" 
                loading={otpLoading || loginLoading}
                style={{ width: '100%', height: '36px', borderRadius: '4px', fontSize: '0.875rem', fontWeight: 400 }}
              >
                {otpSent ? 'Verify OTP & Sign In' : 'Request Login OTP'}
              </Button>

              {otpSent && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <Button 
                    type="link" 
                    onClick={() => { setOtpSent(false); setLoginData({ ...loginData, otp: '' }); }}
                    style={{ color: '#2563eb', fontWeight: 400, fontSize: '0.82rem', padding: 0 }}
                  >
                    ← Back to change email
                  </Button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    );
  }

  const renderOverviewDashboard = () => {
    const counts = analytics?.counts || {};
    const statusEntries = Object.entries(analytics?.tickets?.byStatus || {});
    const softwareEntries = Object.entries(analytics?.tickets?.bySoftware || {});
    const upcoming = analytics?.billing?.upcomingExpiry || [];
    const recentTickets = analytics?.tickets?.recent || [];
    const recentAudit = analytics?.audit || [];

    return (
      <div style={{ width: '100%' }}>
        {/* Large Stat Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
          {[
            { label: 'Live Clients', value: counts.clients || 0, color: '#16a34a', icon: <Users size={18} color="#16a34a" /> },
            { label: 'Leads / Trials', value: counts.leads || 0, color: '#2563eb', icon: <Target size={18} color="#2563eb" /> },
            { label: 'Open Tickets', value: counts.openTickets || 0, color: '#ea580c', icon: <Ticket size={18} color="#ea580c" /> },
            { label: 'Completed Tickets', value: counts.completedTickets || 0, color: '#059669', icon: <CheckCircle2 size={18} color="#059669" /> },
            { label: 'Expired Licenses', value: counts.expired || 0, color: '#dc2626', icon: <AlertCircle size={18} color="#dc2626" /> },
            { label: 'Due Soon (14 Days)', value: counts.upcomingExpiry || 0, color: '#d97706', icon: <Clock size={18} color="#d97706" /> }
          ].map((item) => (
            <div 
              key={item.label} 
              style={{ 
                borderRadius: '4px', 
                padding: '1.25rem 1.4rem', 
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '0.75rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.8125rem', color: '#64748b', fontWeight: 500 }}>{item.label}</span>
                <div style={{ width: '32px', height: '32px', borderRadius: '4px', background: `${item.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.icon}
                </div>
              </div>
              <div style={{ fontSize: '1.875rem', fontWeight: 500, color: '#0f172a', lineHeight: 1 }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Section Grids - Structured Panels */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)' }}>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '0.95rem', fontWeight: 500, color: '#0f172a' }}>Ticket Distribution</h3>
            {[...statusEntries, ...softwareEntries].length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No ticket analytics yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {[...statusEntries, ...softwareEntries].map(([label, value]) => {
                  const total = Math.max(counts.tickets || 1, 1);
                  const width = Math.max(6, Math.round((Number(value) / total) * 100));
                  return (
                    <div key={label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.4rem', color: '#334155' }}>
                        <span style={{ fontWeight: 400 }}>{label}</span>
                        <span style={{ fontWeight: 500, color: '#0f172a' }}>{String(value)}</span>
                      </div>
                      <div style={{ height: '7px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${width}%`, background: '#2563eb', borderRadius: '4px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)' }}>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '0.95rem', fontWeight: 500, color: '#0f172a' }}>Licenses Due Soon</h3>
            {upcoming.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No licenses expiring in the next 14 days.</div>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {upcoming.map((client: any) => (
                  <div key={`${client.software}-${client._id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#0f172a' }}>{client.name}</div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{client.software}</div>
                    </div>
                    <span className={`badge badge-${client.daysRemaining <= 0 ? 'expired' : 'demo'}`}>{client.daysRemaining} days</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)' }}>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '0.95rem', fontWeight: 500, color: '#0f172a' }}>Recent Tickets</h3>
            {recentTickets.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No tickets yet.</div>
            ) : (
              recentTickets.map((ticket: any) => (
                <div key={ticket._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', borderBottom: '1px solid #f1f5f9', padding: '0.75rem 0' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {ticket.subject}
                      {ticket.adminRead === false && <span style={{ background: '#ef4444', color: '#fff', fontSize: '0.65rem', padding: '0.1rem 0.35rem', borderRadius: '4px', fontWeight: 400 }}>NEW</span>}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{ticket.clientName} · {ticket.software}</div>
                  </div>
                  <Tag color={ticket.status === 'Completed' ? 'green' : ticket.status === 'Accepted' ? 'blue' : ticket.status === 'Pending' ? 'gold' : 'orange'} style={{ borderRadius: '4px', fontWeight: 500, margin: 0 }}>{ticket.status}</Tag>
                </div>
              ))
            )}
          </div>

          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)' }}>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '0.95rem', fontWeight: 500, color: '#0f172a' }}>Recent Admin Actions</h3>
            {recentAudit.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No admin audit records yet.</div>
            ) : (
              recentAudit.map((log: any) => (
                <div key={log._id} style={{ borderBottom: '1px solid #f1f5f9', padding: '0.75rem 0' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#0f172a' }}>{log.action}</div>
                  <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{log.actor} · {log.targetName || log.targetType} · {new Date(log.createdAt).toLocaleString()}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBillingDashboard = () => (
    <div>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: '#0f172a' }}>Billing & License Dashboard</h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>Sorted by expired and upcoming renewals.</p>
        </div>
        <Button onClick={() => fetchBillingDashboard(selectedSoftware)} icon={<ReloadOutlined />} size="small" style={{ borderRadius: '4px' }}>
          Refresh
        </Button>
      </div>
      <table className="table">
        <thead style={{ background: '#ffffff' }}>
          <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}><th>Client</th><th>Platform</th><th>Status</th><th>Expiry</th><th>Days Left</th><th style={{ textAlign: 'right' }}>Action</th></tr>
        </thead>
        <tbody>
          {paginatedBillingRows.map((row: any) => (
            <tr key={`${row.software}-${row._id}`}>
              <td><div style={{ fontWeight: 500 }}>{row.name}</div><div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.email}</div></td>
              <td><Tag color="blue" style={{ borderRadius: '4px', margin: 0, fontWeight: 400 }}>{row.software}</Tag></td>
              <td>
                <Tag
                  color={row.status === 'live' ? 'success' : row.status === 'demo' ? 'warning' : 'error'}
                  style={{ borderRadius: '4px', textTransform: 'capitalize', margin: 0, fontWeight: 500 }}
                >
                  {row.status === 'demo' ? 'Trial' : row.status}
                </Tag>
              </td>
              <td>{row.effectiveExpiry ? new Date(row.effectiveExpiry).toLocaleDateString() : 'Lifetime / unset'}</td>
              <td style={{ fontWeight: 500, color: row.daysRemaining !== null && row.daysRemaining <= 7 ? '#dc2626' : '#334155' }}>
                {row.daysRemaining === null ? '∞' : row.daysRemaining}
              </td>
              <td style={{ textAlign: 'right' }}>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => { setSelectedSoftware(row.software); openLicenseManager(row, 'info'); }}
                  style={{ borderRadius: '4px' }}
                >
                  Manage
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa', flexWrap: 'wrap', gap: '8px' }}>
        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
          Showing {billingRows.length === 0 ? 0 : (billingPage - 1) * billingPageSize + 1} to {Math.min(billingPage * billingPageSize, billingRows.length)} of {billingRows.length} entries
        </span>
        <Pagination
          current={billingPage}
          pageSize={billingPageSize}
          total={billingRows.length}
          onChange={(p, ps) => {
            setBillingPage(p);
            setBillingPageSize(ps);
          }}
          showSizeChanger
          pageSizeOptions={['10', '25', '50']}
          size="small"
        />
      </div>
    </div>
  );

  const renderAdminAudit = () => (
    <div>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: '#0f172a' }}>Admin Audit Log</h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>Security trail for license, ticket, client, and WhatsApp changes.</p>
        </div>
        <Button onClick={() => fetchAdminAuditLogs(selectedSoftware)} icon={<ReloadOutlined />} size="small" style={{ borderRadius: '4px' }}>
          Refresh
        </Button>
      </div>
      {adminAuditLogs.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>No admin audit records yet.</div>
      ) : (
        <>
          <table className="table">
            <thead style={{ background: '#ffffff' }}>
              <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}><th>Action</th><th>Target</th><th>Actor</th><th>Reason</th><th style={{ textAlign: 'right' }}>Timestamp</th></tr>
            </thead>
            <tbody>
              {paginatedAuditLogs.map((log: any) => {
                let tagColor: any = 'default';
                if (log.action.includes('activate') || log.action.includes('approve') || log.action.includes('extend')) tagColor = 'success';
                else if (log.action.includes('cancel') || log.action.includes('delete') || log.action.includes('revert')) tagColor = 'error';
                else tagColor = 'processing';
                
                return (
                  <tr key={log._id}>
                    <td>
                      <Tag color={tagColor} style={{ borderRadius: '4px', textTransform: 'capitalize', margin: 0, fontWeight: 500 }}>
                        {log.action}
                      </Tag>
                    </td>
                    <td>
                      <div 
                        style={{ fontWeight: 500, cursor: log.targetType === 'client' ? 'pointer' : 'default', color: log.targetType === 'client' ? '#2563eb' : 'inherit' }}
                        onClick={() => {
                          if (log.targetType === 'client') {
                            setSelectedSoftware(log.software as 'Synapse' | 'Co-HR');
                            setView('clients');
                            setSelectedClientId(log.targetId);
                            setSearchTerm('');
                          }
                        }}
                      >
                        {log.targetName || log.targetType}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{log.software || 'Help Desk'}</div>
                    </td>
                    <td>{log.actor}</td>
                    <td style={{ color: '#64748b' }}>{log.reason || '-'}</td>
                    <td style={{ textAlign: 'right', fontSize: '0.8rem', color: '#64748b' }}>{new Date(log.createdAt).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              Showing {adminAuditLogs.length === 0 ? 0 : (auditPage - 1) * auditPageSize + 1} to {Math.min(auditPage * auditPageSize, adminAuditLogs.length)} of {adminAuditLogs.length} entries
            </span>
            <Pagination
              current={auditPage}
              pageSize={auditPageSize}
              total={adminAuditLogs.length}
              onChange={(p, ps) => {
                setAuditPage(p);
                setAuditPageSize(ps);
              }}
              showSizeChanger
              pageSizeOptions={['10', '25', '50']}
              size="small"
            />
          </div>
        </>
      )}
    </div>
  );

  const renderUpdatesPage = () => {
    const sectionColors: Record<string, { bg: string; color: string; border: string }> = {
      'Security': { bg: '#fef2f2', color: '#991b1b', border: '#fee2e2' },
      'Fixed':    { bg: '#fff7ed', color: '#92400e', border: '#fed7aa' },
      'Added':    { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
      'Changed':  { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
      'Removed':  { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
    };

    const apps = [
      { key: 'crm', label: 'Synapse CRM (frontend + backend + landing page)', data: systemStatus?.crm },
      { key: 'helpdesk', label: 'Help Desk (this app)', data: systemStatus?.helpdesk },
      { key: 'mobile', label: 'Synapse Employee (mobile app)', data: systemStatus?.mobile }
    ];

    return (
      <div style={{ padding: '2rem', maxWidth: '900px' }}>
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
            Live version and changelog data for every Saasbuck app, fetched directly from each app's own backend (or, for the mobile app, from GitHub) — not a hand-maintained page that can drift out of sync with what's actually deployed.
          </p>
        </div>

        {systemStatusLoading && !systemStatus ? (
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', padding: '2rem 0', textAlign: 'center' }}>Loading live version data…</div>
        ) : !systemStatus ? (
          <div style={{ fontSize: '0.85rem', color: '#be123c', padding: '2rem 0', textAlign: 'center' }}>Could not reach the backend for version data.</div>
        ) : (
          apps.map(({ key, label, data }) => {
            const unavailable = data?.available === false;
            const loc = data?.loc || {};
            const totalLines = unavailable
              ? null
              : Object.values(loc).reduce((sum: number, v: any) => sum + (v?.available ? v.lines : 0), 0);

            return (
              <div key={key} style={{ marginBottom: '2.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e2e8f0' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>{label}</h3>
                  {!unavailable && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{totalLines?.toLocaleString()} lines · commit {data?.gitCommit}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', padding: '0.15rem 0.5rem', border: '1px solid #dbeafe' }}>v{data?.version}</span>
                    </div>
                  )}
                </div>

                {unavailable ? (
                  <div style={{ fontSize: '0.8rem', color: '#be123c', padding: '0.75rem 0' }}>
                    Unavailable right now{data?.error ? `: ${data.error}` : ''} — not showing a stale or fake number.
                  </div>
                ) : (!data?.changelog || data.changelog.length === 0) ? (
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', padding: '0.75rem 0' }}>No changelog entries yet.</div>
                ) : (
                  data.changelog.map((entry: any, i: number) => (
                    <div key={i} style={{ marginBottom: '1.25rem', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)' }}>
                      <div style={{ padding: '1rem 1.25rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', fontFamily: 'monospace' }}>v{entry.version}</span>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{entry.date}</span>
                      </div>
                      <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {Object.entries(entry.sections || {}).map(([section, bullets]: [string, any]) => {
                          const colors = sectionColors[section] || sectionColors['Changed'];
                          return (
                            <div key={section}>
                              <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: '99px', background: colors.bg, color: colors.color, border: `1px solid ${colors.border}` }}>
                                {section.toUpperCase()}
                              </span>
                              <ul style={{ margin: '0.4rem 0 0', padding: '0 0 0 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                {bullets.map((b: string, bi: number) => (
                                  <li key={bi} style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.6 }}>{b}</li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderTeamManagement = () => {
    const roleOptions = [
      { value: 'support', label: 'Support Agent (Tickets Only)' },
      { value: 'admin', label: 'Administrator (Full Business Control)' },
      { value: 'developer', label: 'Developer / Tech Support' },
      { value: 'custom', label: 'Custom Permissions' }
    ];

    const softwareOptions = [
      { label: 'All Platforms (Global)', value: 'All' },
      { label: 'Synapse CRM', value: 'Synapse' },
      { label: 'Co-HR Suite', value: 'Co-HR' }
    ];

    const activeCount = teamMembers.filter(m => m.status === 'active').length;
    const disabledCount = teamMembers.filter(m => m.status === 'disabled').length;

    return (
      <div style={{ padding: '0.5rem 0', width: '100%' }}>
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 500, color: '#0f172a' }}>
              Staff & Team Access Management
            </h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: '#64748b' }}>
              Configure account isolation, role permissions, and platform access for company staff members.
            </p>
          </div>
          {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageTeam) && (
            <Button
              type="primary"
              icon={<UserPlus size={16} style={{ verticalAlign: '-2px' }} />}
              size="middle"
              onClick={() => {
                setEditingStaffId(null);
                setStaffFormData({
                  name: '',
                  email: '',
                  role: 'support',
                  password: '',
                  permissions: {
                    manageLicenses: false,
                    manageClients: false,
                    manageTickets: true,
                    manageWhatsApp: false,
                    viewAuditLogs: false,
                    manageTeam: false
                  },
                  softwareAccess: ['All']
                });
                setIsStaffModalOpen(true);
              }}
              style={{ fontWeight: 400, height: '36px', padding: '0 16px', borderRadius: '4px', background: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              Register Staff Member
            </Button>
          )}
        </div>

        {/* Large Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
          <div style={{ borderRadius: '4px', padding: '1.25rem 1.4rem', background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#64748b' }}>Total Staff</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 500, color: '#0f172a', marginTop: '0.25rem', lineHeight: 1 }}>{teamMembers.length}</div>
            </div>
            <div style={{ width: '36px', height: '36px', borderRadius: '4px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={20} color="#2563eb" />
            </div>
          </div>

          <div style={{ borderRadius: '4px', padding: '1.25rem 1.4rem', background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#166534' }}>Active Accounts</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 500, color: '#15803d', marginTop: '0.25rem', lineHeight: 1 }}>{activeCount}</div>
            </div>
            <div style={{ width: '36px', height: '36px', borderRadius: '4px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserCheck size={20} color="#16a34a" />
            </div>
          </div>

          <div style={{ borderRadius: '4px', padding: '1.25rem 1.4rem', background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#991b1b' }}>Deactivated</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 500, color: '#dc2626', marginTop: '0.25rem', lineHeight: 1 }}>{disabledCount}</div>
            </div>
            <div style={{ width: '36px', height: '36px', borderRadius: '4px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserX size={20} color="#dc2626" />
            </div>
          </div>
        </div>

        {/* Staff Table */}
        {isTeamLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading staff records...</div>
        ) : teamMembers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: '#94a3b8' }}>
            <Users size={36} style={{ marginBottom: 12, color: '#cbd5e1' }} />
            <div>No staff members registered yet.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#ffffff' }}>
                <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Staff Member</th>
                  <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', width: '110px' }}>Role</th>
                  <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', width: '110px' }}>Status</th>
                  <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Active Permissions</th>
                  <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Platform Access</th>
                  <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Last Login</th>
                  <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map(member => {
                  const isSuper = member.role === 'superadmin';
                  const roleTagColor = isSuper ? 'volcano' : member.role === 'admin' ? 'blue' : member.role === 'support' ? 'cyan' : member.role === 'developer' ? 'purple' : 'default';

                  return (
                    <tr key={member._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '4px', background: isSuper ? '#fee2e2' : '#eff6ff', color: isSuper ? '#dc2626' : '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, fontSize: '0.85rem' }}>
                            {member.name ? member.name[0].toUpperCase() : 'U'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500, color: '#0f172a', fontSize: '0.875rem' }}>{member.name}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{member.email}</span>
                              {member.hasPassword ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '10px', color: '#16a34a', background: '#dcfce7', padding: '1px 5px', borderRadius: '4px', fontWeight: 400 }}>
                                  <Lock size={10} /> Password
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '10px', color: '#ca8a04', background: '#fef9c3', padding: '1px 5px', borderRadius: '4px', fontWeight: 400 }}>
                                  OTP only
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <Tag color={roleTagColor} style={{ textTransform: 'capitalize', fontWeight: 500, fontSize: '11px', borderRadius: '4px', margin: 0 }}>
                          {member.role === 'superadmin' ? 'Super Admin' : member.role}
                        </Tag>
                      </td>
                      <td style={{ padding: '0.85rem 1rem', verticalAlign: 'middle' }}>
                        <Tag color={member.status === 'active' ? 'success' : 'default'} style={{ borderRadius: '4px', fontWeight: 500, margin: 0 }}>
                          {member.status === 'active' ? 'Active' : 'Disabled'}
                        </Tag>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        {isSuper ? (
                          <Tag color="blue" style={{ fontWeight: 500, fontSize: '11px', borderRadius: '4px', margin: 0 }}>Full Super Admin Access</Tag>
                        ) : (
                          <Space size={[4, 4]} wrap>
                            {member.permissions?.manageLicenses && <Tag color="blue" style={{ borderRadius: '4px', fontWeight: 400, fontSize: '11px' }}>Licenses</Tag>}
                            {member.permissions?.manageClients && <Tag color="cyan" style={{ borderRadius: '4px', fontWeight: 400, fontSize: '11px' }}>Clients</Tag>}
                            {member.permissions?.manageTickets && <Tag color="green" style={{ borderRadius: '4px', fontWeight: 400, fontSize: '11px' }}>Tickets</Tag>}
                            {member.permissions?.manageWhatsApp && <Tag color="geekblue" style={{ borderRadius: '4px', fontWeight: 400, fontSize: '11px' }}>WhatsApp</Tag>}
                            {member.permissions?.viewAuditLogs && <Tag color="gold" style={{ borderRadius: '4px', fontWeight: 400, fontSize: '11px' }}>Audit</Tag>}
                            {member.permissions?.manageTeam && <Tag color="purple" style={{ borderRadius: '4px', fontWeight: 400, fontSize: '11px' }}>Team</Tag>}
                            {!Object.values(member.permissions || {}).some(Boolean) && (
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>None</span>
                            )}
                          </Space>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <Space size={[4, 4]} wrap>
                          {(member.softwareAccess || ['All']).map((sw, idx) => (
                            <Tag key={idx} color="default" style={{ fontSize: '11px', fontWeight: 400, borderRadius: '4px', display: 'inline-flex', alignItems: 'center' }}>
                              <Layers size={11} style={{ marginRight: 4 }} />
                              {sw === 'All' ? 'All Platforms' : sw}
                            </Tag>
                          ))}
                        </Space>
                      </td>
                      <td style={{ padding: '0.85rem 1rem', fontSize: '0.78rem', color: '#64748b' }}>
                        {member.lastLoginAt ? new Date(member.lastLoginAt).toLocaleString() : 'Never'}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                        <Space size="small">
                          <Button
                            size="small"
                            icon={<Edit3 size={12} style={{ verticalAlign: '-1px' }} />}
                            disabled={isSuper && !userProfile?.isSuperAdmin}
                            onClick={() => {
                              setEditingStaffId(member._id);
                              setStaffFormData({
                                name: member.name,
                                email: member.email,
                                role: member.role,
                                password: '',
                                permissions: { ...member.permissions },
                                softwareAccess: [...(member.softwareAccess || ['All'])]
                              });
                              setIsStaffModalOpen(true);
                            }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', borderRadius: '4px', fontWeight: 400 }}
                          >
                            Edit
                          </Button>
                          {!isSuper && (
                            <>
                              <Button
                                size="small"
                                onClick={() => handleToggleStaffStatus(member)}
                                style={{
                                  color: member.status === 'active' ? '#dc2626' : '#16a34a',
                                  borderColor: member.status === 'active' ? '#fecaca' : '#bbf7d0',
                                  background: member.status === 'active' ? '#fef2f2' : '#f0fdf4',
                                  fontWeight: 400,
                                  borderRadius: '4px'
                                }}
                              >
                                {member.status === 'active' ? 'Disable' : 'Enable'}
                              </Button>
                              <Button
                                size="small"
                                danger
                                icon={<Trash2 size={12} style={{ verticalAlign: '-1px' }} />}
                                onClick={() => handleDeleteStaff(member)}
                                style={{ borderRadius: '4px' }}
                              />
                            </>
                          )}
                        </Space>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Ant Design Modal for Register / Edit Staff */}
        <Modal
          open={isStaffModalOpen}
          onCancel={() => setIsStaffModalOpen(false)}
          title={
            <div style={{ fontSize: '1.05rem', fontWeight: 500, color: '#0f172a' }}>
              {editingStaffId ? 'Edit Staff Member & Permissions' : 'Register New Staff Member'}
            </div>
          }
          footer={[
            <Button key="cancel" onClick={() => setIsStaffModalOpen(false)}>
              Cancel
            </Button>,
            <Button
              key="save"
              type="primary"
              onClick={handleSaveStaffMember}
              style={{ borderRadius: '4px' }}
            >
              {editingStaffId ? 'Save Changes' : 'Register Staff'}
            </Button>
          ]}
          width={600}
          centered
          styles={{ body: { maxHeight: 'calc(80vh - 120px)', overflowY: 'auto', paddingRight: '8px' } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
            {/* Full Name Field */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                Full Name
              </label>
              <Input
                prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                placeholder="e.g. Rahul Sharma"
                value={staffFormData.name}
                onChange={e => setStaffFormData({ ...staffFormData, name: e.target.value })}
                style={{ borderRadius: '4px' }}
              />
            </div>

            {/* Gmail / Email Field */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                Staff Gmail / Email Address
              </label>
              <Input
                prefix={<MailOutlined style={{ color: '#94a3b8' }} />}
                type="email"
                disabled={Boolean(editingStaffId)}
                placeholder="e.g. rahul.support@gmail.com"
                value={staffFormData.email}
                onChange={e => setStaffFormData({ ...staffFormData, email: e.target.value })}
                style={{ borderRadius: '4px' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <LockOutlined style={{ fontSize: '12px' }} />
                Only this Gmail account will be permitted to sign into the helpdesk.
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                {editingStaffId ? "Update Password (Optional)" : "Initial Password (Optional)"}
              </label>
              <Input.Password
                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                placeholder={editingStaffId ? "Leave blank to keep existing password" : "Optional password for direct login"}
                value={staffFormData.password}
                onChange={e => setStaffFormData({ ...staffFormData, password: e.target.value })}
                style={{ borderRadius: '4px' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <LockOutlined style={{ fontSize: '12px' }} />
                {editingStaffId 
                  ? "Leave blank to keep current password, or enter a new password to update." 
                  : "If set, the staff member can sign in using this password in addition to OTP."}
              </div>
            </div>

            {/* Role Preset Dropdown */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                Role Preset
              </label>
              <Select
                value={staffFormData.role}
                style={{ width: '100%' }}
                onChange={(role: any) => {
                  let perms = { ...staffFormData.permissions };
                  if (role === 'admin') {
                    perms = { manageLicenses: true, manageClients: true, manageTickets: true, manageWhatsApp: true, viewAuditLogs: true, manageTeam: false };
                  } else if (role === 'support') {
                    perms = { manageLicenses: false, manageClients: false, manageTickets: true, manageWhatsApp: false, viewAuditLogs: false, manageTeam: false };
                  } else if (role === 'developer') {
                    perms = { manageLicenses: false, manageClients: true, manageTickets: true, manageWhatsApp: true, viewAuditLogs: true, manageTeam: false };
                  }
                  setStaffFormData({ ...staffFormData, role, permissions: perms });
                }}
                options={roleOptions}
              />
            </div>

            <Divider style={{ margin: '8px 0', fontSize: '0.78rem', textTransform: 'none', color: '#64748b', fontWeight: 500 }}>
              Granular Access Permissions
            </Divider>

            {/* Checkbox Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px' }}>
              {[
                { key: 'manageLicenses', title: 'Manage Licenses & Expiry', desc: 'Activate subscriptions, extend trial demo periods, and cancel licenses.' },
                { key: 'manageClients', title: 'Manage Clients & Data', desc: 'Edit client tags, view custom notes, login-as, and delete clients.' },
                { key: 'manageTickets', title: 'Manage Support Tickets', desc: 'Update ticket status, post admin replies, and add remarks.' },
                { key: 'manageWhatsApp', title: 'WhatsApp API Config', desc: 'Configure Meta API tokens, Phone IDs, and send test messages.' },
                { key: 'viewAuditLogs', title: 'Security Audit Trail', desc: 'Inspect security events, admin actions, and change logs.' },
                { key: 'manageTeam', title: 'Team & Staff Access', desc: 'Register new staff accounts and customize role permissions.' }
              ].map(item => {
                const isChecked = Boolean((staffFormData.permissions as any)[item.key]);
                return (
                  <Tooltip key={item.key} title={item.desc} placement="top">
                    <div
                      onClick={() => setStaffFormData({
                        ...staffFormData,
                        role: 'custom',
                        permissions: { ...staffFormData.permissions, [item.key]: !isChecked }
                      })}
                      style={{ 
                        cursor: 'pointer', 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 14px',
                        borderRadius: '4px',
                        background: isChecked ? '#eff6ff' : '#f8fafc',
                        border: `1px solid ${isChecked ? '#93c5fd' : '#e2e8f0'}`,
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <Checkbox checked={isChecked} />
                      <span style={{ fontSize: '0.85rem', fontWeight: isChecked ? 500 : 400, color: isChecked ? '#1d4ed8' : '#1e293b' }}>
                        {item.title}
                      </span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>

            <Divider style={{ margin: '8px 0', fontSize: '0.78rem', textTransform: 'none', color: '#64748b', fontWeight: 500 }}>
              Platform Isolation & Access
            </Divider>

            {/* Platform Selection */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {softwareOptions.map(option => {
                const isSelected = staffFormData.softwareAccess.includes(option.value as any);
                return (
                  <div
                    key={option.value}
                    onClick={() => {
                      let next = [...staffFormData.softwareAccess];
                      if (option.value === 'All') {
                        next = isSelected ? [] : ['All'];
                      } else {
                        next = next.filter(s => s !== 'All');
                        if (isSelected) next = next.filter(s => s !== option.value);
                        else next.push(option.value as any);
                        if (next.length === 0) next = ['All'];
                      }
                      setStaffFormData({ ...staffFormData, softwareAccess: next });
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      background: isSelected ? '#eff6ff' : '#f8fafc',
                      border: `1px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: isSelected ? 500 : 400,
                      color: isSelected ? '#1d4ed8' : '#334155',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Checkbox checked={isSelected} />
                    <span>{option.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      </div>
    );
  };

  const renderPlansPage = () => {
    return (
      <PlansManagementView
        selectedSoftware={selectedSoftware}
        plans={plans}
        isPlansLoading={isPlansLoading}
        featureDefinitions={featureDefinitions}
      />
    );
  };


  const renderSidebar = () => {
    const allowedSoftwares = userProfile?.softwareAccess || ['All'];
    const canSeeAll = userProfile?.isSuperAdmin || allowedSoftwares.includes('All');
    const canSeeSynapse = canSeeAll || allowedSoftwares.includes('Synapse');
    const canSeeCoHr = canSeeAll || allowedSoftwares.includes('Co-HR');

    const softwareMenuItems: MenuProps['items'] = [];

    if (canSeeAll) {
      softwareMenuItems.push({
        key: 'all',
        label: 'All Platforms (Global)',
        onClick: () => {
          setSelectedSoftware(null);
          setView('clients');
          setSelectedClientId(null);
          setSelectedTicket(null);
          setSearchTerm('');
        }
      });
      softwareMenuItems.push({ type: 'divider' });
    }

    if (canSeeSynapse) {
      softwareMenuItems.push({
        key: 'Synapse',
        label: 'Synapse CRM',
        onClick: () => {
          setSelectedSoftware('Synapse');
          setView('clients');
          setSelectedClientId(null);
          setSelectedTicket(null);
          setSearchTerm('');
        }
      });
    }

    if (canSeeCoHr) {
      softwareMenuItems.push({
        key: 'Co-HR',
        label: 'Co-HR Suite',
        onClick: () => {
          setSelectedSoftware('Co-HR');
          setView('clients');
          setSelectedClientId(null);
          setSelectedTicket(null);
          setSearchTerm('');
        }
      });
    }

    const currentSoftwareLabel = !selectedSoftware 
      ? 'All Platforms (Global)' 
      : (selectedSoftware === 'Synapse' ? 'Synapse CRM' : 'Co-HR Suite');

    return (
      <aside className="sidebar">
        {/* Simple Clean Header Aligned with Page Header */}
        <div 
          className="fb-sidebar-header" 
          onClick={() => { setSelectedSoftware(null); setView('clients'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              background: '#ffffff', 
              border: '1px solid #e2e8f0', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
            }}>
              <img 
                src="/sb-logo-black.png" 
                alt="Saasbuck" 
                style={{ width: '22px', height: '22px', objectFit: 'contain' }} 
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="fb-business-title">Saasbuck Help Desk</div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 400, lineHeight: 1.2 }}>Management Portal</div>
            </div>
          </div>
        </div>

        {/* Ant Design Platform Dropdown Button */}
        <div className="fb-dropdown-container">
          <Dropdown menu={{ items: softwareMenuItems }} trigger={['click']}>
            <button className="antd-platform-dropdown-btn">
              <span className="antd-dropdown-label">{currentSoftwareLabel}</span>
              <ChevronDown size={13} color="#65676b" />
            </button>
          </Dropdown>
        </div>

        {/* Navigation Links */}
        <div className="sidebar-nav">
          {!selectedSoftware ? (
            <>
              <div className="fb-nav-section-title">Manage Business</div>
              <button 
                className={`fb-nav-link ${view === 'clients' ? 'active' : ''}`}
                onClick={() => { setView('clients'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Home size={18} />
                </div>
                <span className="fb-nav-text">Overview</span>
              </button>
              
              <button 
                className={`fb-nav-link ${view === 'tickets' ? 'active' : ''}`}
                onClick={() => { setView('tickets'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchTickets(); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Headset size={18} />
                </div>
                <span className="fb-nav-text">Support Tickets</span>
                {tickets.filter(t => t.status === 'Open').length > 0 && (
                  <span className="fb-nav-badge">{tickets.filter(t => t.status === 'Open').length}</span>
                )}
              </button>

              <button 
                className={`fb-nav-link ${view === 'billing' ? 'active' : ''}`}
                onClick={() => { setView('billing'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchBillingDashboard(null); }}
              >
                <div className="fb-nav-icon-wrap">
                  <WalletCards size={18} />
                </div>
                <span className="fb-nav-text">Billing & Licenses</span>
              </button>

              <button 
                className={`fb-nav-link ${view === 'plans' ? 'active' : ''}`}
                onClick={() => { setView('plans'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchPlans(); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Package size={18} />
                </div>
                <span className="fb-nav-text">Plan Packages</span>
              </button>

              <div className="fb-nav-divider"></div>

              <div className="fb-nav-section-title">Security & Governance</div>
              
              {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageTeam) && (
                <button 
                  className={`fb-nav-link ${view === 'team' ? 'active' : ''}`}
                  onClick={() => { setView('team'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchTeamMembers(); }}
                >
                  <div className="fb-nav-icon-wrap">
                    <UserCheck size={18} />
                  </div>
                  <span className="fb-nav-text">Team & Staff</span>
                </button>
              )}

              {(userProfile?.isSuperAdmin || userProfile?.permissions?.viewAuditLogs) && (
                <button 
                  className={`fb-nav-link ${view === 'admin-audit' ? 'active' : ''}`}
                  onClick={() => { setView('admin-audit'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchAdminAuditLogs(null); }}
                >
                  <div className="fb-nav-icon-wrap">
                    <Shield size={18} />
                  </div>
                  <span className="fb-nav-text">Audit Log</span>
                </button>
              )}

              <button 
                className={`fb-nav-link ${view === 'updates' ? 'active' : ''}`}
                onClick={() => { setView('updates'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchSystemStatus(); }}
              >
                <div className="fb-nav-icon-wrap">
                  <ClipboardList size={18} />
                </div>
                <span className="fb-nav-text">Updates & Changelog</span>
              </button>
            </>
          ) : (
            <>
              <div className="fb-nav-section-title">{selectedSoftware} Suite</div>
              <button 
                className={`fb-nav-link ${view === 'clients' ? 'active' : ''}`}
                onClick={() => { setView('clients'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Users size={18} />
                </div>
                <span className="fb-nav-text">Active Clients</span>
              </button>
              
              <button 
                className={`fb-nav-link ${view === 'leads' ? 'active' : ''}`}
                onClick={() => { setView('leads'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Target size={18} />
                </div>
                <span className="fb-nav-text">Leads & Trials</span>
              </button>

              <button 
                className={`fb-nav-link ${view === 'billing' ? 'active' : ''}`}
                onClick={() => { setView('billing'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchBillingDashboard(selectedSoftware); }}
              >
                <div className="fb-nav-icon-wrap">
                  <WalletCards size={18} />
                </div>
                <span className="fb-nav-text">Billing & Licenses</span>
              </button>

              <button 
                className={`fb-nav-link ${view === 'plans' ? 'active' : ''}`}
                onClick={() => { setView('plans'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); fetchPlans(selectedSoftware || 'Synapse'); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Package size={18} />
                </div>
                <span className="fb-nav-text">Plan Packages</span>
              </button>
              
              <button 
                className={`fb-nav-link ${view === 'activity-logs' ? 'active' : ''}`}
                onClick={() => { setView('activity-logs'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Activity size={18} />
                </div>
                <span className="fb-nav-text">Activity Logs</span>
              </button>

              <div className="fb-nav-divider"></div>

              <button 
                className="fb-nav-link fb-nav-link-return"
                onClick={() => { setSelectedSoftware(null); setView('clients'); setSelectedClientId(null); setSelectedTicket(null); setSearchTerm(''); }}
              >
                <div className="fb-nav-icon-wrap">
                  <Home size={18} />
                </div>
                <span className="fb-nav-text">All Platforms</span>
              </button>
            </>
          )}
        </div>

        {/* Sidebar Footer with User Profile & Role */}
        <div className="fb-sidebar-footer">
          <div className="user-profile-badge">
            <div className="user-profile-avatar" style={{ borderRadius: '50%', overflow: 'hidden' }}>
              {(userProfile?.name || 'A')[0].toUpperCase()}
            </div>
            <div className="user-profile-meta">
              <div className="user-profile-name">{userProfile?.name || 'Staff User'}</div>
              <div className="user-profile-role">{userProfile?.isSuperAdmin ? 'Super Admin' : (userProfile?.role || 'Staff')}</div>
            </div>
          </div>
          <button 
            className="fb-logout-full-btn" 
            onClick={handleLogout}
            title="Log Out"
          >
            <LogOut size={16} />
            <span>Log Out</span>
          </button>
        </div>
      </aside>
    );
  };

  const renderContentHeader = () => {
    const showSearchFilter = (view === 'clients' || view === 'leads' || view === 'tickets') && !selectedTicket && !selectedClientId && Boolean(selectedSoftware || view === 'tickets');
    
    let viewTitle = '';
    if (view === 'updates') viewTitle = 'System Updates & Changelog';
    else if (view === 'team') viewTitle = 'Internal Staff & Access Management';
    else if (view === 'plans') viewTitle = `Plan Packages & Pricing - ${selectedSoftware || 'Synapse CRM'}`;
    else if (view === 'activity-logs') viewTitle = `Subscription Activity Logs - ${selectedSoftware}`;
    else if (view === 'billing') viewTitle = `Billing & License Dashboard - ${selectedSoftware || 'All Platforms'}`;
    else if (view === 'admin-audit') viewTitle = 'Admin Audit Log';
    else if (view === 'tickets') {
      if (selectedTicket) viewTitle = 'Ticket Details';
      else if (selectedClientId) viewTitle = `Tickets for ${groupedTickets.find(g => g.groupKey === selectedClientId)?.clientName || 'Client'}`;
      else viewTitle = 'Global Support Tickets';
    } else {
      const prefix = selectedSoftware ? `${selectedSoftware} ` : '';
      viewTitle = `${prefix}${view === 'clients' ? 'Clients' : 'Leads'}`;
    }

    return (
      <header className="content-header">
        <div className="content-header-top">
          <h2 className="content-header-title">
            {viewTitle}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {view === 'activity-logs' && (
              <Button
                icon={<ReloadOutlined />}
                onClick={() => selectedSoftware && fetchGlobalLogs(selectedSoftware)}
                style={{ borderRadius: '4px', fontWeight: 400 }}
              >
                Refresh Logs
              </Button>
            )}
            {view === 'clients' && selectedSoftware === 'Synapse' && !selectedClientId && userProfile?.isSuperAdmin && (
              <Badge count={pendingOfflinePayments.length} offset={[-4, 4]}>
                <Button
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => {
                    fetchPendingOfflinePayments();
                    setIsPendingApprovalsModalOpen(true);
                  }}
                  style={{
                    borderRadius: '4px',
                    borderColor: pendingOfflinePayments.length > 0 ? '#f59e0b' : '#d9d9d9',
                    color: pendingOfflinePayments.length > 0 ? '#b45309' : '#475569',
                    background: pendingOfflinePayments.length > 0 ? '#fffbeb' : '#ffffff',
                    fontWeight: 500
                  }}
                >
                  Pending Approvals
                </Button>
              </Badge>
            )}
            {view === 'clients' && selectedSoftware === 'Synapse' && !selectedClientId && userProfile?.isSuperAdmin && (
              <Badge count={renewalAlerts.filter(a => a.urgency === 'critical' || a.urgency === 'expired' || a.urgency === 'urgent').length} offset={[-4, 4]}>
                <Button
                  icon={<BellOutlined />}
                  onClick={() => {
                    fetchRenewalAlerts();
                    setIsRenewalAlertsModalOpen(true);
                  }}
                  style={{
                    borderRadius: '4px',
                    borderColor: renewalAlerts.length > 0 ? '#6366f1' : '#d9d9d9',
                    color: renewalAlerts.length > 0 ? '#4338ca' : '#475569',
                    background: renewalAlerts.length > 0 ? '#eef2ff' : '#ffffff',
                    fontWeight: 500
                  }}
                >
                  Renewal Alerts
                </Button>
              </Badge>
            )}
            {view === 'clients' && selectedSoftware === 'Synapse' && !selectedClientId && (userProfile?.isSuperAdmin || userProfile?.permissions?.manageLicenses) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateClientModalOpen(true)}
                style={{ borderRadius: '4px', background: '#2563eb', fontWeight: 400 }}
              >
                Create Client
              </Button>
            )}
          </div>
        </div>

        {showSearchFilter && (
          <div className="search-filter-bar">
            <Input
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder={`Search ${view}...`}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setClientPage(1);
                setTicketPage(1);
                setGroupedTicketPage(1);
              }}
              allowClear
              style={{ width: 260, borderRadius: '4px' }}
            />
            <Select
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val);
                setClientPage(1);
                setTicketPage(1);
                setGroupedTicketPage(1);
              }}
              style={{ width: 160, borderRadius: '4px' }}
              options={
                view === 'tickets' ? [
                  { value: 'all', label: 'All Tickets' },
                  { value: 'Open', label: 'Open' },
                  { value: 'Accepted', label: 'Accepted' },
                  { value: 'Pending', label: 'Pending' },
                  { value: 'Completed', label: 'Completed' },
                ] : [
                  { value: 'all', label: 'All Status' },
                  { value: 'live', label: 'Live Licenses' },
                  { value: 'demo', label: 'Trial Accounts' },
                  { value: 'expired', label: 'Expired' },
                ]
              }
            />
            {view === 'tickets' && !selectedTicket && !selectedClientId && (
              <>
                <Select
                  value={ticketSoftwareFilter}
                  onChange={(val) => {
                    setTicketSoftwareFilter(val);
                    setTicketPage(1);
                  }}
                  style={{ width: 140, borderRadius: '4px' }}
                  options={[
                    { value: 'all', label: 'All Platforms' },
                    { value: 'Synapse', label: 'Synapse' },
                    { value: 'Co-HR', label: 'Co-HR' },
                  ]}
                />
                <Select
                  value={ticketPriorityFilter}
                  onChange={(val) => {
                    setTicketPriorityFilter(val);
                    setTicketPage(1);
                  }}
                  style={{ width: 140, borderRadius: '4px' }}
                  options={[
                    { value: 'all', label: 'All Priorities' },
                    { value: 'Low', label: 'Low Priority' },
                    { value: 'Medium', label: 'Medium Priority' },
                    { value: 'High', label: 'High Priority' },
                    { value: 'Urgent', label: 'Urgent Priority' },
                  ]}
                />
                <Button
                  onClick={() => setTicketViewMode(prev => prev === 'flat' ? 'grouped' : 'flat')}
                  style={{ borderRadius: '4px', fontWeight: 400 }}
                >
                  {ticketViewMode === 'flat' ? 'Group by Client' : 'Flat View'}
                </Button>
              </>
            )}
          </div>
        )}
      </header>
    );
  };

  if (isMobileBlocked) {
    return (
      <div className="mobile-barrier">
        <div className="mobile-barrier-card">
          <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <h2>Mobile Not Supported</h2>
          <p>Please use our dedicated native mobile application for the best experience on smaller screens.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      {renderSidebar()}

      <main className="main-content">
        {renderContentHeader()}

        <div className="content-body" style={{ padding: '1.5rem 2rem' }}>
          {!selectedSoftware && view === 'clients' ? (
            loading ? (
              <div style={{ textAlign: 'center', padding: '4rem' }}>
                <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Loading dashboard...</div>
              </div>
            ) : renderOverviewDashboard()
          ) : (
            loading ? (
              <div style={{ textAlign: 'center', padding: '4rem' }}>
                <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Syncing data...</div>
              </div>
            ) : view === 'team' ? (
              renderTeamManagement()
            ) : view === 'plans' ? (
              renderPlansPage()
            ) : view === 'updates' ? (
              renderUpdatesPage()
            ) : view === 'billing' ? (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', overflowX: 'auto' }}>
                {renderBillingDashboard()}
              </div>
            ) : view === 'admin-audit' ? (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', overflowX: 'auto' }}>
                {renderAdminAudit()}
              </div>
            ) : view === 'activity-logs' ? (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', overflowX: 'auto' }}>
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: '#0f172a' }}>Subscription Activity Logs - {selectedSoftware}</h3>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => selectedSoftware && fetchGlobalLogs(selectedSoftware)}
                    style={{ borderRadius: '4px', fontWeight: 400 }}
                  >
                    Refresh
                  </Button>
                </div>
                {globalLogs.length === 0 ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>No subscription logs recorded yet.</div>
                ) : (
                  <table className="table">
                    <thead style={{ background: '#ffffff' }}>
                      <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                        <th>Account / Client</th>
                        <th>Action</th>
                        <th>Details</th>
                        <th style={{ textAlign: 'right' }}>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLogs.map((log: any) => {
                        let tagColor = 'default';
                        const act = (log.action || '').toLowerCase();
                        if (act === 'login' || act === 'register') tagColor = 'success';
                        else if (act === 'subscribe' || act === 'update') tagColor = 'processing';
                        else if (act === 'unsubscribe' || act === 'logout') tagColor = 'error';

                        return (
                          <tr key={log.id}>
                            <td style={{ fontWeight: 500, color: '#0f172a' }}>{log.user?.name || 'System'}</td>
                            <td>
                              <Tag color={tagColor} style={{ borderRadius: '4px', fontWeight: 500, textTransform: 'capitalize', fontSize: '11px' }}>
                                {log.action}
                              </Tag>
                            </td>
                            <td style={{ fontSize: '0.88rem', color: '#334155' }}>{log.description}</td>
                            <td style={{ textAlign: 'right', color: '#64748b', fontSize: '0.8rem' }}>{new Date(log.createdAt).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa', flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Showing {globalLogs.length === 0 ? 0 : (logsPage - 1) * logsPageSize + 1} to {Math.min(logsPage * logsPageSize, globalLogs.length)} of {globalLogs.length} logs
                  </span>
                  <Pagination
                    current={logsPage}
                    pageSize={logsPageSize}
                    total={globalLogs.length}
                    onChange={(p, ps) => {
                      setLogsPage(p);
                      setLogsPageSize(ps);
                    }}
                    showSizeChanger
                    pageSizeOptions={['15', '25', '50', '100']}
                    size="small"
                  />
                </div>
              </div>
            ) : view === 'tickets' ? (
              selectedTicket ? (
                <div className="ticket-detail" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', padding: '1.75rem' }}>
                  {/* Top Bar with Back and Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #f1f5f9', paddingBottom: '1.25rem', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <Button 
                        icon={<ArrowLeftOutlined />} 
                        onClick={() => setSelectedTicket(null)} 
                        style={{ borderRadius: '4px', fontWeight: 400, marginBottom: '0.75rem' }}
                      >
                        Back to Tickets
                      </Button>
                      <h2 style={{ fontSize: '1.25rem', fontWeight: 500, color: '#0f172a', margin: '0 0 0.5rem 0' }}>
                        {selectedTicket.subject}
                      </h2>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.82rem', color: '#64748b', flexWrap: 'wrap' }}>
                        <span>Client: <strong style={{ color: '#1e293b', fontWeight: 500 }}>{selectedTicket.clientName}</strong> ({selectedTicket.clientEmail})</span>
                        <span>•</span>
                        <span>Platform: <Tag color={selectedTicket.software === 'Synapse' ? 'blue' : 'cyan'} style={{ borderRadius: '4px', fontWeight: 500 }}>{selectedTicket.software}</Tag></span>
                        <span>•</span>
                        <span>Created: {new Date(selectedTicket.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <Tag 
                        color={
                          selectedTicket.status === 'Completed' ? 'green' :
                          selectedTicket.status === 'Accepted' ? 'blue' :
                          selectedTicket.status === 'Pending' ? 'gold' : 'orange'
                        }
                        style={{ borderRadius: '4px', padding: '3px 10px', fontSize: '0.8rem', fontWeight: 500 }}
                      >
                        {selectedTicket.status}
                      </Tag>
                      <Tag 
                        color={
                          selectedTicket.priority === 'Urgent' ? 'red' :
                          selectedTicket.priority === 'High' ? 'volcano' :
                          selectedTicket.priority === 'Medium' ? 'gold' : 'default'
                        }
                        style={{ borderRadius: '4px', padding: '3px 10px', fontSize: '0.8rem', fontWeight: 500 }}
                      >
                        {selectedTicket.priority} Priority
                      </Tag>
                    </div>
                  </div>

                  {/* Quick Status Action Buttons */}
                  {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageTickets) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>Quick Actions:</span>
                      <Button
                        size="small"
                        onClick={() => handleStatusUpdate(selectedTicket._id, 'Accepted')}
                        style={{ borderRadius: '4px', fontWeight: 400, borderColor: '#bfdbfe', background: '#eff6ff', color: '#1d4ed8' }}
                      >
                        Mark Accepted
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleStatusUpdate(selectedTicket._id, 'Pending')}
                        style={{ borderRadius: '4px', fontWeight: 400, borderColor: '#fde68a', background: '#fffbeb', color: '#b45309' }}
                      >
                        Mark Pending
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleStatusUpdate(selectedTicket._id, 'Completed')}
                        style={{ borderRadius: '4px', fontWeight: 400, borderColor: '#bbf7d0', background: '#f0fdf4', color: '#15803d' }}
                      >
                        Mark Completed
                      </Button>
                    </div>
                  )}

                  {/* Top Grid: Ticket Description & Engineering Remarks */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
                    <Col xs={24} md={12}>
                      <Card 
                        title={<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#334155' }}>Ticket Description</span>} 
                        size="small" 
                        style={{ height: '100%', borderRadius: '4px', borderColor: '#e2e8f0', boxShadow: 'none' }}
                      >
                        <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem', lineHeight: '1.6', color: '#334155', minHeight: '110px' }}>
                          {selectedTicket.message}
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card 
                        title={<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#334155' }}>Engineering Remarks & Resolution</span>} 
                        size="small" 
                        style={{ height: '100%', borderRadius: '4px', borderColor: '#e2e8f0', boxShadow: 'none' }}
                      >
                        <Input.TextArea
                          value={adminRemarksText}
                          onChange={(e) => setAdminRemarksText(e.target.value)}
                          placeholder="Enter internal diagnostic notes or resolution summary..."
                          rows={4}
                          style={{ borderRadius: '4px', fontSize: '0.88rem' }}
                        />
                        {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageTickets) && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                            <Button
                              type="primary"
                              onClick={async () => {
                                await handleStatusUpdate(selectedTicket._id, selectedTicket.status, adminRemarksText);
                                Swal.fire({ icon: 'success', title: 'Remarks Saved', text: 'Engineering remarks updated successfully', timer: 1500, showConfirmButton: false });
                              }}
                              style={{ borderRadius: '4px', fontWeight: 400, background: '#2563eb' }}
                            >
                              Save Remarks
                            </Button>
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>

                  {/* Bottom Grid: Conversation Thread & Audit Timeline */}
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={14}>
                      <Card 
                        title={<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#334155' }}>Conversation & Reply Thread</span>} 
                        size="small" 
                        style={{ borderRadius: '4px', borderColor: '#e2e8f0', boxShadow: 'none' }}
                      >
                        <div style={{ maxHeight: '340px', overflowY: 'auto', padding: '0.5rem 0.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {(selectedTicket.comments || []).length === 0 ? (
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
                              No messages or updates posted yet.
                            </div>
                          ) : (
                            (selectedTicket.comments || []).map((comment: any, index: number) => {
                              const isAdmin = comment.sender === 'admin';
                              return (
                                <div 
                                  key={`${comment.createdAt}-${index}`} 
                                  style={{
                                    padding: '0.75rem 1rem',
                                    borderRadius: '4px',
                                    background: isAdmin ? '#eff6ff' : '#f8fafc',
                                    border: `1px solid ${isAdmin ? '#bfdbfe' : '#e2e8f0'}`,
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <Avatar 
                                        size={22} 
                                        style={{ 
                                          background: isAdmin ? '#2563eb' : '#64748b', 
                                          fontSize: '11px',
                                          fontWeight: 600
                                        }}
                                      >
                                        {(comment.authorName || comment.sender || 'U')[0].toUpperCase()}
                                      </Avatar>
                                      <span style={{ fontWeight: 500, fontSize: '0.82rem', color: isAdmin ? '#1d4ed8' : '#334155' }}>
                                        {comment.authorName || comment.sender}
                                      </span>
                                      <Tag color={isAdmin ? 'blue' : 'default'} style={{ fontSize: '10px', borderRadius: '4px', margin: 0, padding: '0 4px', lineHeight: '16px' }}>
                                        {isAdmin ? 'Staff Support' : 'Client'}
                                      </Tag>
                                    </div>
                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                      {new Date(comment.createdAt).toLocaleString()}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.88rem', color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                    {comment.message}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        <Divider style={{ margin: '1rem 0' }} />

                        {/* Reply Composer */}
                        <div style={{ background: '#fafafa', padding: '0.75rem', borderRadius: '4px', border: '1px solid #f1f5f9' }}>
                          <Input.TextArea
                            value={ticketComment}
                            onChange={(e) => setTicketComment(e.target.value)}
                            placeholder="Type a response or client-visible update..."
                            rows={3}
                            style={{ borderRadius: '4px', fontSize: '0.88rem' }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                            <Button 
                              type="primary" 
                              icon={<SendOutlined />} 
                              onClick={addTicketComment} 
                              disabled={!ticketComment.trim()} 
                              style={{ borderRadius: '4px', fontWeight: 400, background: '#2563eb' }}
                            >
                              Send Reply
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} lg={10}>
                      <Card 
                        title={<span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#334155' }}>Activity & Audit Timeline</span>} 
                        size="small" 
                        style={{ height: '100%', borderRadius: '4px', borderColor: '#e2e8f0', boxShadow: 'none' }}
                      >
                        <div style={{ maxHeight: '420px', overflowY: 'auto', padding: '0.5rem 0.25rem' }}>
                          {(selectedTicket.history || []).length === 0 ? (
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '2rem' }}>
                              No audit history recorded.
                            </div>
                          ) : (
                            <Timeline
                              items={(selectedTicket.history || []).slice().reverse().map((item: any, idx: number) => ({
                                color: item.type === 'status_change' ? 'blue' : 'gray',
                                children: (
                                  <div key={idx}>
                                    <div style={{ fontWeight: 500, fontSize: '0.82rem', color: '#1e293b' }}>
                                      {item.message || item.type}
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '0.72rem', marginTop: '2px' }}>
                                      {item.actor || 'System'} · {new Date(item.createdAt).toLocaleString()}
                                    </div>
                                  </div>
                                )
                              }))}
                            />
                          )}
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </div>
              ) : selectedClientId ? (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', overflowX: 'auto' }}>
                  <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button 
                      icon={<ArrowLeftOutlined />} 
                      onClick={() => { setSelectedClientId(null); setSearchTerm(''); }} 
                      style={{ borderRadius: '4px', fontWeight: 400 }}
                    >
                      All Clients
                    </Button>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 500, color: '#0f172a' }}>
                      Tickets for {groupedTickets.find(g => g.groupKey === selectedClientId)?.clientName}
                    </h3>
                    <Tag color="blue" style={{ borderRadius: '4px', fontWeight: 500 }}>
                      {groupedTickets.find(g => g.groupKey === selectedClientId)?.ticketCount} Tickets
                    </Tag>
                  </div>
                  <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#ffffff' }}>
                      <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Subject</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Status</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Priority</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Date</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(groupedTickets.find(g => g.groupKey === selectedClientId)?.tickets || [])
                        .filter((t: any) => {
                          const subject = t.subject || '';
                          return subject.toLowerCase().includes(searchTerm.toLowerCase()) && (statusFilter === 'all' || t.status === statusFilter);
                        })
                        .map((ticket: any) => (
                          <tr key={ticket._id}>
                            <td style={{ fontWeight: 500 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {ticket.subject}
                                {ticket.adminRead === false && (
                                  <Tag color="red" style={{ borderRadius: '4px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>NEW</Tag>
                                )}
                              </div>
                            </td>
                            <td>
                              <Tag color={ticket.status === 'Completed' ? 'green' : ticket.status === 'Accepted' ? 'blue' : ticket.status === 'Pending' ? 'gold' : 'orange'} style={{ borderRadius: '4px', fontWeight: 500 }}>
                                {ticket.status}
                              </Tag>
                            </td>
                            <td>
                              <Tag color={ticket.priority === 'Urgent' ? 'red' : ticket.priority === 'High' ? 'volcano' : ticket.priority === 'Medium' ? 'gold' : 'default'} style={{ borderRadius: '4px', fontWeight: 500 }}>
                                {ticket.priority}
                              </Tag>
                            </td>
                            <td style={{ fontSize: '0.82rem', color: '#64748b' }}>{new Date(ticket.createdAt).toLocaleDateString()}</td>
                            <td style={{ textAlign: 'right' }}>
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => fetchTicketDetail(ticket._id)}
                                style={{ borderRadius: '4px', fontWeight: 400, background: '#2563eb' }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : ticketViewMode === 'flat' ? (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', overflowX: 'auto' }}>
                  <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#ffffff' }}>
                      <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Subject</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Client</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Platform</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Status</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Priority</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Date</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTickets.map((ticket: any) => (
                        <tr key={ticket._id}>
                          <td style={{ fontWeight: 500 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {ticket.subject}
                              {ticket.adminRead === false && (
                                <Tag color="red" style={{ borderRadius: '4px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>NEW</Tag>
                              )}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>{ticket.clientName}</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{ticket.clientEmail}</div>
                          </td>
                          <td>
                            <Tag color={ticket.software === 'Synapse' ? 'blue' : 'cyan'} style={{ borderRadius: '4px', fontWeight: 500 }}>
                              {ticket.software}
                            </Tag>
                          </td>
                          <td>
                            <Tag color={ticket.status === 'Completed' ? 'green' : ticket.status === 'Accepted' ? 'blue' : ticket.status === 'Pending' ? 'gold' : 'orange'} style={{ borderRadius: '4px', fontWeight: 500 }}>
                              {ticket.status}
                            </Tag>
                          </td>
                          <td>
                            <Tag color={ticket.priority === 'Urgent' ? 'red' : ticket.priority === 'High' ? 'volcano' : ticket.priority === 'Medium' ? 'gold' : 'default'} style={{ borderRadius: '4px', fontWeight: 500 }}>
                              {ticket.priority}
                            </Tag>
                          </td>
                          <td style={{ fontSize: '0.82rem', color: '#64748b' }}>{new Date(ticket.createdAt).toLocaleDateString()}</td>
                          <td style={{ textAlign: 'right' }}>
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => fetchTicketDetail(ticket._id)}
                              style={{ borderRadius: '4px', fontWeight: 400, background: '#2563eb' }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      Showing {filteredTicketsList.length === 0 ? 0 : (ticketPage - 1) * ticketPageSize + 1} to {Math.min(ticketPage * ticketPageSize, filteredTicketsList.length)} of {filteredTicketsList.length} tickets
                    </span>
                    <Pagination
                      current={ticketPage}
                      pageSize={ticketPageSize}
                      total={filteredTicketsList.length}
                      onChange={(p, ps) => {
                        setTicketPage(p);
                        setTicketPageSize(ps);
                      }}
                      showSizeChanger
                      pageSizeOptions={['10', '25', '50']}
                      size="small"
                    />
                  </div>
                </div>
              ) : (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', overflowX: 'auto' }}>
                  <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#ffffff' }}>
                      <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Client Name</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Software</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', textAlign: 'center' }}>Total Tickets</th>
                        <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedGroupedTickets.map((group) => (
                        <tr key={group.groupKey}>
                          <td>
                            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0f172a' }}>
                              {group.clientName}
                              {group.tickets.some((t: any) => t.adminRead === false) && (
                                <Tag color="red" style={{ borderRadius: '4px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>NEW</Tag>
                              )}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{group.clientEmail}</div>
                          </td>
                          <td>
                            <Tag color={group.software === 'Synapse' ? 'blue' : 'cyan'} style={{ borderRadius: '4px', fontWeight: 500 }}>
                              {group.software}
                            </Tag>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#2563eb', fontSize: '1rem' }}>
                              {statusFilter === 'all' ? group.ticketCount : group.tickets.filter((t: any) => t.status === statusFilter).length}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => { setSelectedClientId(group.groupKey); setSearchTerm(''); }}
                              style={{ borderRadius: '4px', fontWeight: 400, background: '#2563eb' }}
                            >
                              View Tickets
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      Showing {filteredGroupedTickets.length === 0 ? 0 : (groupedTicketPage - 1) * groupedTicketPageSize + 1} to {Math.min(groupedTicketPage * groupedTicketPageSize, filteredGroupedTickets.length)} of {filteredGroupedTickets.length} client groups
                    </span>
                    <Pagination
                      current={groupedTicketPage}
                      pageSize={groupedTicketPageSize}
                      total={filteredGroupedTickets.length}
                      onChange={(p, ps) => {
                        setGroupedTicketPage(p);
                        setGroupedTicketPageSize(ps);
                      }}
                      showSizeChanger
                      pageSizeOptions={['10', '25', '50']}
                      size="small"
                    />
                  </div>
                </div>
              )
            ) : (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)', overflowX: 'auto' }}>
                <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#ffffff' }}>
                    <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Account</th>
                      <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Status</th>
                      <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Plan</th>
                      <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem' }}>Registered</th>
                      <th style={{ padding: '0.8rem 1rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none', fontSize: '0.8125rem', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClients.map((client: any) => (
                      <tr key={client._id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>{client.name || client.username}</div>
                            {(client.tags || []).length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {(client.tags as string[]).map((tag: string) => (
                                  <span key={tag} style={{
                                    background: tag.toLowerCase().includes('custom') ? '#fef3c7' : '#ede9fe',
                                    color: tag.toLowerCase().includes('custom') ? '#92400e' : '#5b21b6',
                                    border: `1px solid ${tag.toLowerCase().includes('custom') ? '#fcd34d' : '#c4b5fd'}`,
                                    fontSize: '0.72rem',
                                    fontWeight: 400,
                                    padding: '1px 6px',
                                    borderRadius: '4px',
                                    textTransform: 'none'
                                  }}>{tag}</span>
                                ))}
                              </div>
                            )}
                            {(() => {
                              const regDate = new Date(client.createdAt);
                              const diffTime = Math.abs(new Date().getTime() - regDate.getTime());
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                              if (diffDays <= 7) {
                                return (
                                  <span style={{
                                    background: '#ecfdf5',
                                    color: '#059669',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    padding: '1px 6px',
                                    borderRadius: '4px',
                                    border: '1px solid #a7f3d0',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    textTransform: 'none'
                                  }}>New</span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{client.email}</div>
                          {client.phone && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.1rem' }}>📞 {client.phone}</div>}
                        </td>
                        <td>{(() => {
                          const isLive = client.subscriptionStatus === 'live';
                          const isDemo = client.subscriptionStatus === 'demo' || (!client.subscriptionStatus && !client.expiryDate);
                          const expiry = client.expiryDate ? new Date(client.expiryDate) : null;
                          const demoExpiry = client.demoExpiryDate ? new Date(client.demoExpiryDate) : null;
                          const now = new Date();
                          
                          const trialDuration = 7 * 24 * 60 * 60 * 1000;
                          const isDemoExpired = isDemo && (demoExpiry ? demoExpiry < now : new Date(client.createdAt).getTime() + trialDuration < now.getTime());
                          const isLiveExpired = isLive && expiry && expiry < now;
                          
                          const isExpired = client.subscriptionStatus === 'expired' || isDemoExpired || isLiveExpired;

                          if (isExpired) {
                            return <span className="badge badge-expired">{isDemo ? 'Trial Expired' : 'Expired'}</span>;
                          }
                          if (isLive) return <span className="badge badge-live">Live</span>;
                          return <span className="badge badge-demo">Trial</span>;
                        })()}</td>
                         <td>
                           {(() => {
                             // client.planName/assignedPlan come straight from the client's real
                             // Entitlement (Synapse) / Company (Co-HR) record, joined against the
                             // live Plan catalog — never a guessed default. See utils/planResolver.js.
                             const c = client.planName
                               ? { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', label: client.planName }
                               : client.assignedPlan
                                 ? { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', label: `${client.assignedPlan} (removed)` }
                                 : { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0', label: 'Unassigned' };
                             return (
                               <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, fontSize: '0.72rem', fontWeight: 500, padding: '2px 8px', borderRadius: '4px', textTransform: 'none' }}>
                                 {c.label}
                               </span>
                             );
                           })()}
                         </td>
                        <td>{new Date(client.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.45rem', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                            {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageLicenses) && (
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => openLicenseManager(client, 'info')}
                                style={{ borderRadius: '4px', fontWeight: 400, background: '#10b981', borderColor: '#10b981' }}
                              >
                                License
                              </Button>
                            )}

                            {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageClients) && (
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => {
                                  setSelectedClientForTags(client);
                                  setClientTags(client.tags || []);
                                  setClientCustomNote(client.customNote || '');
                                  setTagInput('');
                                }}
                                style={{ borderRadius: '4px', fontWeight: 400, background: '#7c3aed', borderColor: '#7c3aed' }}
                              >
                                Tags
                              </Button>
                            )}

                            {client.software === 'Synapse' && (userProfile?.isSuperAdmin || userProfile?.permissions?.manageWhatsApp) && (
                              <Button
                                size="small"
                                type="primary"
                                onClick={async () => {
                                  setSelectedClientForWhatsApp(client);
                                  setWhatsappConfigLoading(true);
                                  try {
                                    const res = await fetch(`${API_URL}/clients/${client._id}/whatsapp`, {
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      setWhatsappConfig(data.data || { accessToken: '', phoneNumberId: '', wabaId: '' });
                                    }
                                  } catch (err) {
                                    Swal.fire('Error', 'Failed to load WhatsApp credentials', 'error');
                                  } finally {
                                    setWhatsappConfigLoading(false);
                                  }
                                }}
                                style={{ borderRadius: '4px', fontWeight: 400, background: '#16a34a', borderColor: '#16a34a' }}
                              >
                                WhatsApp API
                              </Button>
                            )}
                            
                            {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageClients) && (
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => loginAs(client)}
                                style={{ borderRadius: '4px', fontWeight: 400, background: '#0284c7', borderColor: '#0284c7' }}
                              >
                                Access
                              </Button>
                            )}

                            <Button
                              size="small"
                              onClick={() => openLicenseManager(client, 'info')}
                              style={{ borderRadius: '4px', fontWeight: 400, background: '#64748b', borderColor: '#64748b', color: '#fff' }}
                            >
                              Info
                            </Button>

                            {(userProfile?.isSuperAdmin || userProfile?.permissions?.manageClients) && (
                              <Button
                                size="small"
                                danger
                                type="primary"
                                onClick={() => deleteClient(client)}
                                style={{ borderRadius: '4px', fontWeight: 400 }}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa', flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Showing {displayedClients.length === 0 ? 0 : (clientPage - 1) * clientPageSize + 1} to {Math.min(clientPage * clientPageSize, displayedClients.length)} of {displayedClients.length} entries
                  </span>
                  <Pagination
                    current={clientPage}
                    pageSize={clientPageSize}
                    total={displayedClients.length}
                    onChange={(p, ps) => {
                      setClientPage(p);
                      setClientPageSize(ps);
                    }}
                    showSizeChanger
                    pageSizeOptions={['10', '25', '50']}
                    size="small"
                  />
                </div>
              </div>
            )
          )}
        </div>
        <div style={{ textAlign: 'center', padding: '2rem 0', fontSize: '0.75rem', color: '#94a3b8', borderTop: '1px solid #eee', marginTop: 'auto' }}>
          Product by <strong>saasbuck</strong> &copy; {new Date().getFullYear()}
        </div>
      </main>

      {/* Dedicated High-Performance License Manager Window */}
      <LicenseManagerModal
        open={Boolean(selectedClientForLicense)}
        client={selectedClientForLicense}
        initialTab={licenseModalInitialTab}
        onClose={() => setSelectedClientForLicense(null)}
        token={token}
        API_URL={API_URL}
        userProfile={userProfile}
        plans={plans}
        onClientUpdated={(updatedClient) => {
          setSelectedClientForLicense(updatedClient);
          if (selectedSoftware) fetchClients(selectedSoftware);
        }}
      />

      {/* Ant Design Modal for Gated Client Onboarding & Payment Journey */}
      <Modal
        open={isCreateClientModalOpen}
        onCancel={resetCreateClientModal}
        title={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' }}>
              Onboard Client & Quotation Gating
            </div>
            {/* Step Indicator Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.74rem' }}>
              <Tag color={createClientStep === 1 ? 'blue' : 'success'}>
                1. Details & Plan
              </Tag>
              <span style={{ color: '#cbd5e1' }}>&rarr;</span>
              <Tag color={createClientStep === 2 ? 'blue' : createClientStep > 2 ? 'success' : 'default'}>
                2. Quotation Gate
              </Tag>
              <span style={{ color: '#cbd5e1' }}>&rarr;</span>
              <Tag color={createClientStep === 3 ? 'blue' : 'default'}>
                3. Payment & Activation
              </Tag>
            </div>
          </div>
        }
        footer={null}
        width={580}
        centered
        styles={{ body: { maxHeight: 'calc(85vh - 120px)', overflowY: 'auto', paddingRight: '6px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
          {/* STEP 1: Details and Plan Selection */}
          {createClientStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Alert
                type="info"
                showIcon
                description="Enter the client profile and commercial terms. A formal Quotation PDF must be attached in Step 2 before payment links or activations can be unlocked."
                style={{ borderRadius: '6px' }}
              />

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                  Full Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Input
                  prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="e.g. Rahul Sharma"
                  value={createClientForm.name}
                  onChange={e => setCreateClientForm({ ...createClientForm, name: e.target.value })}
                  style={{ borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                  Email Address <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Input
                  prefix={<MailOutlined style={{ color: '#94a3b8' }} />}
                  type="email"
                  placeholder="e.g. rahul@company.com"
                  value={createClientForm.email}
                  onChange={e => setCreateClientForm({ ...createClientForm, email: e.target.value })}
                  style={{ borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                  Phone Number (WhatsApp)
                </label>
                <Input
                  prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="e.g. 9876543210"
                  value={createClientForm.phone}
                  onChange={e => setCreateClientForm({ ...createClientForm, phone: e.target.value })}
                  style={{ borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                  Company / Firm Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Input
                  prefix={<BankOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="e.g. Acme Pvt Ltd"
                  value={createClientForm.company}
                  onChange={e => setCreateClientForm({ ...createClientForm, company: e.target.value })}
                  style={{ borderRadius: '4px' }}
                />
              </div>

              <Row gutter={12}>
                <Col span={12}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                    Seats <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <InputNumber
                    min={1}
                    value={Number(createClientForm.seats) || 1}
                    onChange={val => setCreateClientForm({ ...createClientForm, seats: String(val || 1) })}
                    style={{ width: '100%', borderRadius: '4px' }}
                  />
                </Col>
                <Col span={12}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                    Duration (Months) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <InputNumber
                    min={1}
                    value={Number(createClientForm.months) || 12}
                    onChange={val => setCreateClientForm({ ...createClientForm, months: String(val || 12) })}
                    style={{ width: '100%', borderRadius: '4px' }}
                  />
                </Col>
              </Row>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                  Rate per user/month (₹)
                </label>
                <InputNumber
                  min={0}
                  prefix="₹"
                  placeholder="499"
                  value={createClientForm.pricePerUser ? Number(createClientForm.pricePerUser) : 499}
                  onChange={val => setCreateClientForm({ ...createClientForm, pricePerUser: val !== null ? String(val) : '' })}
                  style={{ width: '100%', borderRadius: '4px' }}
                />
              </div>

              {/* Optional Add-ons Selection */}
              <div style={{ marginTop: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                    Enterprise Add-ons (Optional)
                  </label>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Select extra modules to include</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* WhatsApp Business CRM */}
                  <div
                    onClick={() => {
                      const curr = Boolean(createClientForm.addons?.whatsappCrm);
                      setCreateClientForm({
                        ...createClientForm,
                        addons: { ...createClientForm.addons, whatsappCrm: !curr }
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '6px',
                      border: createClientForm.addons?.whatsappCrm ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                      background: createClientForm.addons?.whatsappCrm ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Checkbox checked={Boolean(createClientForm.addons?.whatsappCrm)} />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>
                          WhatsApp Business CRM & Shared Inbox
                        </div>
                        <div style={{ fontSize: '0.71rem', color: '#64748b' }}>
                          Meta Cloud API, shared team inbox, broadcast sequences
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb' }}>+₹1,999</div>
                      <div style={{ fontSize: '0.67rem', color: '#94a3b8' }}>/ month</div>
                    </div>
                  </div>

                  {/* Live GPS Field Tracking */}
                  <div
                    onClick={() => {
                      const curr = Boolean(createClientForm.addons?.liveTracking);
                      setCreateClientForm({
                        ...createClientForm,
                        addons: { ...createClientForm.addons, liveTracking: !curr }
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '6px',
                      border: createClientForm.addons?.liveTracking ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                      background: createClientForm.addons?.liveTracking ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Checkbox checked={Boolean(createClientForm.addons?.liveTracking)} />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>
                          Live GPS Field Staff Tracking
                        </div>
                        <div style={{ fontSize: '0.71rem', color: '#64748b' }}>
                          Real-time location, selfie check-ins & route history
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb' }}>+₹399</div>
                      <div style={{ fontSize: '0.67rem', color: '#94a3b8' }}>/ user / mo</div>
                    </div>
                  </div>

                  {/* Custom Domain & White-Label */}
                  <div
                    onClick={() => {
                      const curr = Boolean(createClientForm.addons?.customDomain);
                      setCreateClientForm({
                        ...createClientForm,
                        addons: { ...createClientForm.addons, customDomain: !curr }
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '6px',
                      border: createClientForm.addons?.customDomain ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                      background: createClientForm.addons?.customDomain ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Checkbox checked={Boolean(createClientForm.addons?.customDomain)} />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>
                          Custom Domain & White-Label Portal
                        </div>
                        <div style={{ fontSize: '0.71rem', color: '#64748b' }}>
                          Your own domain (crm.yourcompany.com) & custom SSL
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb' }}>+₹999</div>
                      <div style={{ fontSize: '0.67rem', color: '#94a3b8' }}>/ month</div>
                    </div>
                  </div>

                  {/* Priority SLA */}
                  <div
                    onClick={() => {
                      const curr = Boolean(createClientForm.addons?.prioritySla);
                      setCreateClientForm({
                        ...createClientForm,
                        addons: { ...createClientForm.addons, prioritySla: !curr }
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '6px',
                      border: createClientForm.addons?.prioritySla ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                      background: createClientForm.addons?.prioritySla ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Checkbox checked={Boolean(createClientForm.addons?.prioritySla)} />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>
                          Priority SLA & Dedicated Account Manager
                        </div>
                        <div style={{ fontSize: '0.71rem', color: '#64748b' }}>
                          15-min response SLA & VIP dedicated escalation bridge
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb' }}>+₹1,499</div>
                      <div style={{ fontSize: '0.67rem', color: '#94a3b8' }}>/ month</div>
                    </div>
                  </div>

                  {/* VIP Onboarding */}
                  <div
                    onClick={() => {
                      const curr = Boolean(createClientForm.addons?.vipOnboarding);
                      setCreateClientForm({
                        ...createClientForm,
                        addons: { ...createClientForm.addons, vipOnboarding: !curr }
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '6px',
                      border: createClientForm.addons?.vipOnboarding ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                      background: createClientForm.addons?.vipOnboarding ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Checkbox checked={Boolean(createClientForm.addons?.vipOnboarding)} />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>
                          VIP Onboarding & White-Glove Migration
                        </div>
                        <div style={{ fontSize: '0.71rem', color: '#64748b' }}>
                          Hands-on data import from Excel/CRM + staff video training
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb' }}>+₹2,999</div>
                      <div style={{ fontSize: '0.67rem', color: '#94a3b8' }}>one-time</div>
                    </div>
                  </div>

                  {/* Extra Workspaces */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '6px',
                      border: (Number(createClientForm.addons?.additionalWorkspaces) || 0) > 0 ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                      background: (Number(createClientForm.addons?.additionalWorkspaces) || 0) > 0 ? '#eff6ff' : '#fff'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <ClusterOutlined style={{ color: '#2563eb', fontSize: '1rem', marginLeft: '2px' }} />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a' }}>
                          Additional Business Workspaces
                        </div>
                        <div style={{ fontSize: '0.71rem', color: '#64748b' }}>
                          Separate multi-brand workspaces sharing pooled seats
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <InputNumber
                        min={0}
                        max={10}
                        value={Number(createClientForm.addons?.additionalWorkspaces) || 0}
                        onChange={val => {
                          setCreateClientForm({
                            ...createClientForm,
                            addons: { ...createClientForm.addons, additionalWorkspaces: val || 0 }
                          });
                        }}
                        style={{ width: '60px' }}
                      />
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>&times; ₹2,000</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Calculation Summary with Addons */}
              {(() => {
                const seatsNum = Number(createClientForm.seats) || 1;
                const monthsNum = Number(createClientForm.months) || 12;
                const rateNum = createClientForm.pricePerUser ? Number(createClientForm.pricePerUser) : 499;
                const baseSubtotal = seatsNum * monthsNum * rateNum;

                let addonSubtotal = 0;
                const activeAddonBadges: string[] = [];

                if (createClientForm.addons?.whatsappCrm) {
                  const cost = 1999 * monthsNum;
                  addonSubtotal += cost;
                  activeAddonBadges.push(`WhatsApp CRM (₹${cost.toLocaleString('en-IN')})`);
                }
                if (createClientForm.addons?.liveTracking) {
                  const cost = 399 * seatsNum * monthsNum;
                  addonSubtotal += cost;
                  activeAddonBadges.push(`GPS Tracking (₹${cost.toLocaleString('en-IN')})`);
                }
                if (createClientForm.addons?.customDomain) {
                  const cost = 999 * monthsNum;
                  addonSubtotal += cost;
                  activeAddonBadges.push(`Custom Domain (₹${cost.toLocaleString('en-IN')})`);
                }
                if (createClientForm.addons?.prioritySla) {
                  const cost = 1499 * monthsNum;
                  addonSubtotal += cost;
                  activeAddonBadges.push(`Priority SLA (₹${cost.toLocaleString('en-IN')})`);
                }
                if (createClientForm.addons?.vipOnboarding) {
                  const cost = 2999;
                  addonSubtotal += cost;
                  activeAddonBadges.push(`VIP Onboarding (₹${cost.toLocaleString('en-IN')})`);
                }
                const extraWorkspaces = Number(createClientForm.addons?.additionalWorkspaces) || 0;
                if (extraWorkspaces > 0) {
                  const cost = 2000 * extraWorkspaces;
                  addonSubtotal += cost;
                  activeAddonBadges.push(`${extraWorkspaces} Extra Workspace(s) (₹${cost.toLocaleString('en-IN')})`);
                }

                const subtotal = baseSubtotal + addonSubtotal;
                const gst = Math.round(subtotal * 0.18);
                const total = subtotal + gst;

                return (
                  <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b' }}>
                      <span>Base Plan ({seatsNum} seats &times; {monthsNum} mo @ ₹{rateNum}):</span>
                      <span>₹{baseSubtotal.toLocaleString('en-IN')}</span>
                    </div>

                    {addonSubtotal > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#2563eb', marginTop: '4px' }}>
                        <span>Selected Add-ons ({activeAddonBadges.length}):</span>
                        <span style={{ fontWeight: 600 }}>+₹{addonSubtotal.toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    {activeAddonBadges.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '6px 0 2px 0' }}>
                        {activeAddonBadges.map((badge, idx) => (
                          <Tag key={idx} color="blue" style={{ fontSize: '0.68rem', margin: 0 }}>
                            {badge}
                          </Tag>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>
                      <span>Net Subtotal:</span>
                      <span>₹{subtotal.toLocaleString('en-IN')}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>
                      <span>GST (18% SAC 998313):</span>
                      <span>₹{gst.toLocaleString('en-IN')}</span>
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.94rem', fontWeight: 700, color: '#0f172a' }}>
                      <span>Total Payable:</span>
                      <span style={{ color: '#2563eb', fontSize: '1.05rem' }}>₹{total.toLocaleString('en-IN')}</span>
                    </div>
                  </Card>
                );
              })()}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
                <Button onClick={resetCreateClientModal}>Cancel</Button>
                <Button
                  type="primary"
                  loading={createClientSaving}
                  onClick={handleProceedToQuotation}
                  style={{ borderRadius: '4px' }}
                >
                  Proceed to Quotation Gate &rarr;
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: Commercial Quotation & Proposal Verification */}
          {createClientStep === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>
                    Commercial Proposal & Quotation
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: '2px' }}>
                    Verify the itemized proposal with all selected add-ons before issuing payment instructions.
                  </div>
                </div>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={handlePreviewQuotation}
                  style={{ borderColor: '#2563eb', color: '#2563eb', fontWeight: 600 }}
                >
                  Preview Quotation
                </Button>
              </div>

              {/* Itemized Proposal Breakdown Card */}
              <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                  Commercial Summary: {draftPayment?.company || createClientForm.company}
                </div>
                
                <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 0', color: '#475569' }}>
                        Base CRM ({draftPayment?.seats || createClientForm.seats} seats &times; {draftPayment?.months || createClientForm.months} mo @ ₹{draftPayment?.pricePerUser || createClientForm.pricePerUser || 499}):
                      </td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>
                        ₹{Number(draftPayment?.baseAmount || ((Number(draftPayment?.seats || createClientForm.seats || 1)) * (Number(draftPayment?.pricePerUser || createClientForm.pricePerUser || 499)) * (Number(draftPayment?.months || createClientForm.months || 12)))).toLocaleString('en-IN')}
                      </td>
                    </tr>

                    {draftPayment?.addonItems && draftPayment.addonItems.length > 0 ? (
                      draftPayment.addonItems.map((item: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 0', color: '#2563eb' }}>
                            <Tag color="blue" style={{ fontSize: '0.7rem', margin: 0, marginRight: '6px' }}>Add-on</Tag>
                            {item.name}
                          </td>
                          <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: '#2563eb' }}>
                            +₹{Number(item.amount || 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} style={{ padding: '4px 0', fontSize: '0.72rem', color: '#94a3b8' }}>
                          Standard CRM package (no add-ons selected)
                        </td>
                      </tr>
                    )}

                    <tr style={{ borderTop: '1px solid #cbd5e1' }}>
                      <td style={{ padding: '6px 0', color: '#64748b' }}>Net Subtotal:</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>
                        ₹{Number(draftPayment?.subtotal || 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '4px 0', color: '#64748b' }}>GST (18% SAC 998313):</td>
                      <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 600 }}>
                        ₹{Number(draftPayment?.gstAmount || 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                    <tr style={{ borderTop: '2px solid #e2e8f0', fontSize: '0.88rem', fontWeight: 800 }}>
                      <td style={{ padding: '8px 0', color: '#0f172a' }}>Total Proposal Value:</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#2563eb' }}>
                        ₹{Number(draftPayment?.totalAmount || 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Card>

              {/* Fast-track Official Auto-Generation */}
              <div style={{
                background: '#f0fdf4',
                border: '1.5px solid #86efac',
                borderRadius: '8px',
                padding: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#166534' }}>
                    Auto-Generate Official Saasbuck Quotation
                  </div>
                  <div style={{ fontSize: '0.74rem', color: '#15803d', marginTop: '2px' }}>
                    Generates digital proposal with SAC code, add-on breakdown & bank remittance details.
                  </div>
                </div>
                <Button
                  type="primary"
                  icon={<FilePdfOutlined />}
                  loading={uploadingQuotation}
                  onClick={handleAutoGenerateQuotation}
                  style={{ background: '#16a34a', borderColor: '#16a34a', fontWeight: 700, borderRadius: '4px' }}
                >
                  Generate & Proceed &rarr;
                </Button>
              </div>

              {/* Alternative: Upload signed quotation PDF */}
              <div style={{
                border: '1px dashed #cbd5e1',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                background: '#f8fafc'
              }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>
                  Or Upload Custom / Client-Signed Quotation PDF
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={e => {
                    if (e.target.files && e.target.files[0]) {
                      setQuotationFile(e.target.files[0]);
                    }
                  }}
                  style={{ marginTop: '8px', fontSize: '0.78rem' }}
                />
                <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  {quotationFile && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<UploadOutlined />}
                      loading={uploadingQuotation}
                      onClick={handleUploadQuotation}
                    >
                      Upload Selected PDF
                    </Button>
                  )}
                  <Button
                    size="small"
                    icon={<FilePdfOutlined />}
                    loading={uploadingQuotation}
                    onClick={handleUseSampleQuotation}
                    style={{ borderColor: '#6366f1', color: '#6366f1' }}
                  >
                    Use Sample PDF
                  </Button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <Button onClick={() => setCreateClientStep(1)} disabled={uploadingQuotation}>
                  &larr; Back
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Payment & Activation Mode */}
          {createClientStep === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{
                padding: '10px 12px',
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Space>
                  <CheckCircleFilled style={{ color: '#059669' }} />
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#065f46' }}>
                    Quotation Verified & Attached
                  </span>
                </Space>
                {draftPayment?.quotationUrl && (
                  <a
                    href={draftPayment.quotationUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '0.76rem', color: '#059669', textDecoration: 'underline' }}
                  >
                    View PDF
                  </a>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Segmented
                  value={paymentModeTab}
                  onChange={(val) => setPaymentModeTab(val as 'link' | 'offline')}
                  options={[
                    { label: 'Razorpay Payment Link', value: 'link', icon: <CreditCardOutlined /> },
                    { label: 'Record Offline Bank Transfer', value: 'offline', icon: <BankOutlined /> }
                  ]}
                />
              </div>

              {/* Mode A: Razorpay Link */}
              {paymentModeTab === 'link' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Card size="small" style={{ background: '#f8fafc', borderColor: '#e2e8f0', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.82rem', color: '#475569' }}>Payable by Client:</span>
                      <span style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                        ₹{Number(draftPayment?.totalAmount || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Link Validity Period:</span>
                      <Select
                        size="small"
                        value={linkExpireHours}
                        onChange={(val) => setLinkExpireHours(val)}
                        disabled={Boolean(generatedPaymentLink)}
                        style={{ width: 170 }}
                        options={[
                          { label: '24 Hours (1 Day)', value: 24 },
                          { label: '48 Hours (2 Days)', value: 48 },
                          { label: '72 Hours (3 Days)', value: 72 },
                          { label: '7 Days (1 Week)', value: 168 }
                        ]}
                      />
                    </div>
                  </Card>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: '#f1f5f9', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.74rem', color: '#334155' }}>
                    <img src="/sb-logo-black.png" alt="Saasbuck" style={{ height: '16px', width: 'auto', display: 'block' }} />
                    <span>Checkout branding: <strong>Saasbuck</strong> with monochrome black logo</span>
                  </div>

                  {!generatedPaymentLink ? (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                      <Button
                        type="primary"
                        icon={<CreditCardOutlined />}
                        loading={generatingLink}
                        onClick={handleGeneratePaymentLink}
                        style={{ borderRadius: '4px', minWidth: 200 }}
                      >
                        Generate Razorpay Link
                      </Button>
                      <div style={{ marginTop: '8px', fontSize: '0.74rem', color: '#64748b' }}>
                        Produces a live <code>rzp.io/l/...</code> payment checkout link expiring in {linkExpireHours}h.
                      </div>
                      {Boolean(userProfile?.isSuperAdmin) && (
                        <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                          <Button
                            size="small"
                            icon={<ExperimentOutlined />}
                            loading={simulatingPayment}
                            onClick={handleSimulatePayment}
                            style={{ borderColor: '#8b5cf6', color: '#7c3aed', fontSize: '0.76rem' }}
                          >
                            🧪 Fast-track Test: Simulate Webhook Directly (Owner Only)
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <Alert
                        type="success"
                        showIcon
                        title="Payment Link Active"
                        description="Share this checkout link with the client. When paid, subscription activates automatically and password setup email is dispatched."
                        style={{ borderRadius: '6px' }}
                      />
                      {paymentLinkExpiresAt && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '4px', fontSize: '0.76rem', color: '#b45309' }}>
                          <ClockCircleOutlined />
                          <span><strong>Link Expiry:</strong> Valid until {new Date(paymentLinkExpiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, {new Date(paymentLinkExpiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} ({linkExpireHours}h limit)</span>
                        </div>
                      )}
                      <Input
                        value={generatedPaymentLink}
                        readOnly
                        suffix={
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              navigator.clipboard.writeText(generatedPaymentLink);
                              Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Link copied!', timer: 1500, showConfirmButton: false });
                            }}
                          >
                            Copy
                          </Button>
                        }
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {whatsappShareUrl && (
                          <Button
                            icon={<WhatsAppOutlined />}
                            style={{ background: '#25D366', color: '#fff', borderColor: '#25D366', flex: 1 }}
                            onClick={() => window.open(whatsappShareUrl, '_blank')}
                          >
                            Send via WhatsApp
                          </Button>
                        )}
                        <Button
                          type="primary"
                          onClick={() => {
                            resetCreateClientModal();
                            if (selectedSoftware) fetchClients(selectedSoftware);
                          }}
                          style={{ flex: 1 }}
                        >
                          Done
                        </Button>
                      </div>

                      {/* 🧪 Developer / Owner Test Simulation Card - STRICTLY OWNER ONLY */}
                      {Boolean(userProfile?.isSuperAdmin) && (
                        <div style={{ 
                          marginTop: '6px', 
                          padding: '12px 14px', 
                          background: '#f5f3ff', 
                          border: '1px dashed #8b5cf6', 
                          borderRadius: '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#5b21b6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <ExperimentOutlined /> Owner Test Simulator
                            </span>
                            <span style={{ fontSize: '0.68rem', background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                              Owner Exclusive
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: '0.73rem', color: '#6b7280', lineHeight: 1.4 }}>
                            Simulates the Razorpay <code>payment_link.paid</code> webhook. Instantly activates the Synapse subscription, creates the real GST Tax Invoice, and provides the client password setup URL.
                          </p>
                          <Button
                            icon={<ThunderboltFilled />}
                            loading={simulatingPayment}
                            onClick={handleSimulatePayment}
                            style={{
                              background: '#7c3aed',
                              borderColor: '#7c3aed',
                              color: '#ffffff',
                              fontWeight: 600,
                              borderRadius: '4px'
                            }}
                          >
                            Simulate Paid Webhook & Activate Client Now
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Mode B: Offline Bank Transfer */}
              {paymentModeTab === 'offline' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Alert
                    type={userProfile?.isSuperAdmin ? 'info' : 'warning'}
                    showIcon
                    description={
                      userProfile?.isSuperAdmin
                        ? 'Enter verified bank transfer reference (NEFT / RTGS / IMPS). As Saasbuck Owner, confirming this will immediately verify the deposit, generate the GST Tax Invoice, and activate the subscription.'
                        : 'Anti-Fraud Protected: Enter client bank transfer reference (NEFT / RTGS / IMPS). Submitting will queue this payment for Saasbuck Owner bank statement verification before subscription activation.'
                    }
                    style={{ borderRadius: '6px' }}
                  />

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                      Bank Reference / UTR Number <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <Input
                      placeholder="e.g. HDFCR5202610010048123"
                      value={offlineForm.utrNumber}
                      onChange={e => setOfflineForm({ ...offlineForm, utrNumber: e.target.value })}
                      style={{ borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                      Bank Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <Input
                      placeholder="e.g. HDFC Bank Ltd"
                      value={offlineForm.bankName}
                      onChange={e => setOfflineForm({ ...offlineForm, bankName: e.target.value })}
                      style={{ borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                      Remarks / Cheque Details (Optional)
                    </label>
                    <Input.TextArea
                      rows={2}
                      placeholder="e.g. Direct bank transfer verified against statement"
                      value={offlineForm.notes}
                      onChange={e => setOfflineForm({ ...offlineForm, notes: e.target.value })}
                      style={{ borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '5px' }}>
                      Bank Slip / Receipt Screenshot (Optional)
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="file"
                        id="offline-payment-slip-input"
                        accept="image/*,.pdf"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files?.[0] || null;
                          setOfflineForm(prev => ({ ...prev, slipFile: file }));
                        }}
                      />
                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => document.getElementById('offline-payment-slip-input')?.click()}
                        style={{ borderRadius: '4px' }}
                      >
                        {offlineForm.slipFile ? 'Change Slip File' : 'Upload Bank Transfer Slip'}
                      </Button>
                      {offlineForm.slipFile && (
                        <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <CheckCircleFilled /> {offlineForm.slipFile.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
                    <Button onClick={resetCreateClientModal} disabled={recordingOfflinePayment}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      loading={recordingOfflinePayment}
                      onClick={handleRecordOfflinePayment}
                      style={{ borderRadius: '4px' }}
                    >
                      {userProfile?.isSuperAdmin ? 'Verify Deposit & Activate Client' : 'Submit for Owner Approval'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal for Owner Review of Pending Offline Payments (Anti-Fraud Maker-Checker) */}
      <Modal
        open={isPendingApprovalsModalOpen}
        onCancel={() => setIsPendingApprovalsModalOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SafetyCertificateOutlined style={{ color: '#059669', fontSize: '1.2rem' }} />
            <div>
              <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' }}>
                Offline Payments Pending Verification
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 400 }}>
                Owner Maker-Checker Verification Desk • Reconcile against company bank statements
              </div>
            </div>
          </div>
        }
        footer={[
          <Button key="close" onClick={() => setIsPendingApprovalsModalOpen(false)}>
            Close
          </Button>,
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            onClick={fetchPendingOfflinePayments}
          >
            Refresh
          </Button>
        ]}
        width={780}
        centered
        styles={{ body: { maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' } }}
      >
        <div style={{ padding: '4px 0' }}>
          <Alert
            type="info"
            showIcon
            message="Bank Verification Required"
            description="Sales reps cannot auto-activate offline payments. Please verify that the specified UTR reference and amount have been credited to your Saasbuck bank account before approving."
            style={{ marginBottom: '14px', borderRadius: '6px' }}
          />

          {pendingOfflinePayments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 0', color: '#64748b' }}>
              <CheckCircleFilled style={{ fontSize: '2.5rem', color: '#10b981', marginBottom: '10px' }} />
              <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>No pending offline payments</div>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '4px 0 0' }}>
                All offline payments submitted by staff have been reviewed and processed.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pendingOfflinePayments.map((payment) => (
                <Card
                  key={payment._id}
                  size="small"
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #fde68a',
                    background: '#fffdfa',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                  }}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space>
                        <Tag color="orange">PENDING OWNER APPROVAL</Tag>
                        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>
                          {payment.clientName}
                        </span>
                        {payment.company && (
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            ({payment.company})
                          </span>
                        )}
                      </Space>
                      <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a' }}>
                        ₹{Number(payment.totalAmount || 0).toLocaleString('en-IN')}
                        <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 400, marginLeft: '4px' }}>
                          (incl. GST)
                        </span>
                      </span>
                    </div>
                  }
                >
                  <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
                    <Descriptions.Item label="Client Email">
                      {payment.clientEmail}
                    </Descriptions.Item>
                    <Descriptions.Item label="Plan & Seats">
                      {payment.plan || 'growth'} • {payment.seats || 1} Seat(s) ({payment.months || 12} Mo)
                    </Descriptions.Item>
                    <Descriptions.Item label="Bank Name">
                      <strong>{payment.bankName || 'N/A'}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="UTR / Reference" span={2}>
                      <code style={{ fontSize: '0.82rem', fontWeight: 700, color: '#047857' }}>
                        {payment.utrNumber}
                      </code>
                    </Descriptions.Item>
                    <Descriptions.Item label="Recorded By">
                      <span style={{ color: '#4338ca', fontWeight: 600 }}>
                        {payment.offlineRecordedBy || 'Sales Staff'}
                      </span>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        {new Date(payment.offlineRecordedAt || payment.createdAt).toLocaleString()}
                      </div>
                    </Descriptions.Item>
                    {payment.offlineRemark && (
                      <Descriptions.Item label="Remarks" span={3}>
                        <span style={{ color: '#475569', fontStyle: 'italic' }}>
                          {payment.offlineRemark}
                        </span>
                      </Descriptions.Item>
                    )}
                    {payment.quotationUrl && (
                      <Descriptions.Item label="Quotation" span={3}>
                        <a
                          href={payment.quotationUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#b91c1c', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        >
                          <FilePdfOutlined /> View Quotation PDF
                        </a>
                      </Descriptions.Item>
                    )}
                    {payment.paymentSlipUrl && (
                      <Descriptions.Item label="Payment Slip" span={3}>
                        <a
                          href={payment.paymentSlipUrl.startsWith('http') ? payment.paymentSlipUrl : `${API_URL.replace('/api', '')}${payment.paymentSlipUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 600, textDecoration: 'underline' }}
                        >
                          <FileImageOutlined /> View Bank Transfer Slip / Receipt
                        </a>
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  <div style={{
                    marginTop: '10px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    paddingTop: '8px',
                    borderTop: '1px dashed #e2e8f0'
                  }}>
                    <Button
                      size="small"
                      danger
                      loading={isRejectingOfflineId === payment._id}
                      onClick={() => handleRejectFromPendingModal(payment)}
                    >
                      Reject Payment
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      loading={isApprovingOfflineId === payment._id}
                      onClick={() => handleApproveFromPendingModal(payment)}
                      style={{ background: '#059669', borderColor: '#059669', fontWeight: 600 }}
                    >
                      Verify Deposit & Activate
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal for Automated Client Subscription Renewal Alerts (Retention Engine) */}
      <Modal
        open={isRenewalAlertsModalOpen}
        onCancel={() => setIsRenewalAlertsModalOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BellOutlined style={{ color: '#4f46e5', fontSize: '1.2rem' }} />
            <div>
              <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' }}>
                Client Subscription Renewal Alerts
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 400 }}>
                Automated Retention Engine • Subscriptions expiring within 15 days or recently overdue
              </div>
            </div>
          </div>
        }
        footer={[
          <Button key="close" onClick={() => setIsRenewalAlertsModalOpen(false)}>
            Close
          </Button>,
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            loading={loadingRenewalAlerts}
            onClick={fetchRenewalAlerts}
          >
            Refresh
          </Button>
        ]}
        width={820}
        centered
        styles={{ body: { maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' } }}
      >
        <div style={{ padding: '4px 0' }}>
          {renewalAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 0', color: '#64748b' }}>
              <CheckCircleFilled style={{ fontSize: '2.5rem', color: '#10b981', marginBottom: '10px' }} />
              <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>All Client Subscriptions Healthy</div>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '4px 0 0' }}>
                No active clients have subscriptions expiring in the next 15 days.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {renewalAlerts.map((alert) => {
                const isCrit = alert.urgency === 'critical' || alert.urgency === 'expired';
                return (
                  <Card
                    key={alert.clientId}
                    size="small"
                    style={{
                      borderRadius: '8px',
                      border: isCrit ? '1px solid #fca5a5' : '1px solid #e0e7ff',
                      background: isCrit ? '#fffaf0' : '#f8faff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                          <Tag color={
                            alert.urgency === 'expired' ? 'red' :
                            alert.urgency === 'critical' ? 'volcano' :
                            alert.urgency === 'urgent' ? 'gold' : 'blue'
                          }>
                            {alert.urgency === 'expired' ? 'EXPIRED' :
                             alert.urgency === 'critical' ? `${alert.daysRemaining} DAY(S) LEFT - CRITICAL` :
                             alert.urgency === 'urgent' ? `${alert.daysRemaining} DAYS LEFT - URGENT` :
                             `${alert.daysRemaining} DAYS REMAINING`}
                          </Tag>
                          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>
                            {alert.name}
                          </span>
                          {alert.company && (
                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                              ({alert.company})
                            </span>
                          )}
                        </Space>
                        <span style={{ fontSize: '0.8rem', color: '#475569' }}>
                          Expiry: <strong>{new Date(alert.expiryDate).toLocaleDateString()}</strong>
                        </span>
                      </div>
                    }
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', gap: '16px', color: '#475569', flexWrap: 'wrap' }}>
                        <div><strong>Email:</strong> {alert.email}</div>
                        <div><strong>Phone:</strong> {alert.phone}</div>
                        <div><strong>Current Plan:</strong> {alert.lastPaymentPlan} ({alert.seats} seat{alert.seats > 1 ? 's' : ''})</div>
                      </div>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          const clientMatch = clients.find(c => c._id === alert.clientId);
                          if (clientMatch) {
                            setIsRenewalAlertsModalOpen(false);
                            openLicenseManager(clientMatch, 'billing');
                          } else {
                            window.open(`mailto:${alert.email}?subject=Saasbuck%20Synapse%20Subscription%20Renewal`);
                          }
                        }}
                        style={{ borderRadius: '4px', background: '#4f46e5' }}
                      >
                        Manage & Renew
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </Modal>


      {/* Ant Design Modal for WhatsApp API Configuration */}
      <Modal
        open={Boolean(selectedClientForWhatsApp)}
        onCancel={() => setSelectedClientForWhatsApp(null)}
        title={
          selectedClientForWhatsApp ? (
            <div style={{ paddingBottom: '2px' }}>
              <div style={{ fontSize: '1.05rem', fontWeight: 500, color: '#0f172a' }}>
                WhatsApp API Configuration
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 400 }}>
                {selectedClientForWhatsApp.name || selectedClientForWhatsApp.username || selectedClientForWhatsApp.companyName}
              </div>
            </div>
          ) : null
        }
        footer={[
          <Button key="cancel" onClick={() => setSelectedClientForWhatsApp(null)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={whatsappConfigSaving}
            disabled={whatsappConfigLoading}
            onClick={async () => {
              setWhatsappConfigSaving(true);
              try {
                const res = await fetch(`${API_URL}/clients/${selectedClientForWhatsApp._id}/whatsapp`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify(whatsappConfig)
                });
                const data = await res.json();
                if (data.success) {
                  Swal.fire('Saved', 'WhatsApp configuration updated in CRM settings.', 'success');
                  setSelectedClientForWhatsApp(null);
                } else {
                  Swal.fire('Error', data.message || 'Failed to save configuration.', 'error');
                }
              } catch (err) {
                Swal.fire('Error', 'Network error.', 'error');
              } finally {
                setWhatsappConfigSaving(false);
              }
            }}
            style={{ borderRadius: '4px' }}
          >
            Save Configuration
          </Button>
        ]}
        width={560}
        centered
        styles={{ body: { maxHeight: 'calc(80vh - 120px)', overflowY: 'auto', paddingRight: '6px' } }}
      >
        {selectedClientForWhatsApp && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
            <Alert
              type="warning"
              showIcon
              title="Manual Meta Configuration"
              description="Credentials saved here apply directly to this client's Synapse workspace."
              style={{ borderRadius: '4px' }}
            />

            {whatsappConfigLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: '#64748b' }}>
                Loading WhatsApp configuration...
              </div>
            ) : (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                    Permanent Access Token
                  </label>
                  <Input.Password
                    placeholder="EAABs..."
                    value={whatsappConfig.accessToken || ''}
                    onChange={e => setWhatsappConfig({ ...whatsappConfig, accessToken: e.target.value })}
                    style={{ borderRadius: '4px' }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                    Leave blank to keep existing token unchanged.
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                    Phone Number ID
                  </label>
                  <Input
                    placeholder="1234567890"
                    value={whatsappConfig.phoneNumberId || ''}
                    onChange={e => setWhatsappConfig({ ...whatsappConfig, phoneNumberId: e.target.value })}
                    style={{ borderRadius: '4px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                    WABA ID (WhatsApp Business Account ID)
                  </label>
                  <Input
                    placeholder="1234567890"
                    value={whatsappConfig.wabaId || ''}
                    onChange={e => setWhatsappConfig({ ...whatsappConfig, wabaId: e.target.value })}
                    style={{ borderRadius: '4px' }}
                  />
                </div>

                <Divider style={{ margin: '8px 0', fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.04em' }}>
                  Developer Test Tool
                </Divider>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>
                    Send Test Message
                  </label>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      placeholder="Test Phone (e.g. 919876543210)"
                      value={waTestPhone}
                      onChange={e => setWaTestPhone(e.target.value)}
                      style={{ borderRadius: '4px 0 0 4px' }}
                    />
                    <Button
                      type="default"
                      loading={waTestSending}
                      disabled={waTestSending || whatsappConfigSaving}
                      onClick={async () => {
                        if (!waTestPhone) {
                          Swal.fire('Error', 'Test phone number is required', 'error');
                          return;
                        }
                        setWaTestSending(true);
                        try {
                          const res = await fetch(`${API_URL}/clients/${selectedClientForWhatsApp._id}/whatsapp/test-message`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ to: waTestPhone, body: 'Hello from Synapse Helpdesk Test!' })
                          });
                          const data = await res.json();
                          if (res.ok) {
                            Swal.fire('Sent', 'Message sent successfully!', 'success');
                          } else {
                            Swal.fire('Failed', data.message || 'Delivery failed', 'error');
                          }
                        } catch (err) {
                          Swal.fire('Error', 'Connection failed', 'error');
                        } finally {
                          setWaTestSending(false);
                        }
                      }}
                      style={{ borderRadius: '0 4px 4px 0' }}
                    >
                      Send Test
                    </Button>
                  </Space.Compact>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Ant Design Modal for Client Tags & Custom Notes */}
      <Modal
        open={Boolean(selectedClientForTags)}
        onCancel={() => setSelectedClientForTags(null)}
        title={
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 500, color: '#0f172a' }}>Client Tags & Custom Notes</div>
            {selectedClientForTags && (
              <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 400, marginTop: '2px' }}>
                {selectedClientForTags.name || selectedClientForTags.username} · {selectedClientForTags.email}
              </div>
            )}
          </div>
        }
        footer={[
          <Button key="cancel" onClick={() => setSelectedClientForTags(null)} style={{ borderRadius: '4px', fontWeight: 400 }}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={tagsSaving}
            onClick={async () => {
              setTagsSaving(true);
              try {
                const res = await fetch(`${API_URL}/clients/${selectedClientForTags._id}/tags?software=${selectedClientForTags.software || selectedSoftware}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ tags: clientTags, customNote: clientCustomNote })
                });
                const data = await res.json();
                if (data.success) {
                  setClients((prev: any[]) => prev.map(c =>
                    c._id === selectedClientForTags._id
                      ? { ...c, tags: clientTags, customNote: clientCustomNote }
                      : c
                  ));
                  setSelectedClientForTags(null);
                } else {
                  Swal.fire('Error', data.message || 'Failed to save tags', 'error');
                }
              } catch (err) {
                Swal.fire('Error', 'Network error saving tags', 'error');
              } finally {
                setTagsSaving(false);
              }
            }}
            style={{ borderRadius: '4px', fontWeight: 400, background: '#7c3aed', borderColor: '#7c3aed' }}
          >
            Save Tags
          </Button>
        ]}
        width={560}
        destroyOnHidden
      >
        {selectedClientForTags && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                Add Tags
              </label>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="e.g. custom-invoice, bulk-export, arabic-support..."
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                      e.preventDefault();
                      const newTag = tagInput.trim().replace(/,/g, '');
                      if (newTag && !clientTags.includes(newTag)) {
                        setClientTags([...clientTags, newTag]);
                      }
                      setTagInput('');
                    }
                  }}
                  style={{ borderRadius: '4px 0 0 4px' }}
                />
                <Button
                  type="primary"
                  onClick={() => {
                    const newTag = tagInput.trim().replace(/,/g, '');
                    if (newTag && !clientTags.includes(newTag)) {
                      setClientTags([...clientTags, newTag]);
                    }
                    setTagInput('');
                  }}
                  style={{ borderRadius: '0 4px 4px 0', background: '#7c3aed', borderColor: '#7c3aed', fontWeight: 400 }}
                >
                  Add
                </Button>
              </Space.Compact>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                Press Enter or comma to add a tag
              </div>
            </div>

            <div style={{ minHeight: '60px', background: '#f8fafc', border: '1.5px dashed #e2e8f0', borderRadius: '4px', padding: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignContent: 'flex-start' }}>
              {clientTags.length === 0 ? (
                <span style={{ fontSize: '0.8rem', color: '#cbd5e1', alignSelf: 'center' }}>No tags yet — type above and press Enter</span>
              ) : (
                clientTags.map(tag => (
                  <Tag
                    key={tag}
                    closable
                    onClose={() => setClientTags(clientTags.filter(t => t !== tag))}
                    style={{
                      borderRadius: '4px',
                      fontWeight: 500,
                      fontSize: '0.78rem',
                      background: tag.toLowerCase().includes('custom') ? '#fef3c7' : '#ede9fe',
                      color: tag.toLowerCase().includes('custom') ? '#92400e' : '#5b21b6',
                      borderColor: tag.toLowerCase().includes('custom') ? '#fcd34d' : '#c4b5fd'
                    }}
                  >
                    {tag}
                  </Tag>
                ))
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                Custom Work Notes
              </label>
              <Input.TextArea
                rows={4}
                placeholder="Describe the custom code/integration done for this client (for internal reference only)..."
                value={clientCustomNote}
                onChange={e => setClientCustomNote(e.target.value)}
                style={{ borderRadius: '4px' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                These notes are only visible to admins — helps track what custom code is active for this client.
              </div>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default App;
